import React, { useState, useEffect, useCallback } from 'react';
import RichTextEditor from './RichTextEditor';
import HotelIntegrationTab from './HotelIntegrationTab';
import RoomsManagement from './RoomsManagement';
import NotificationSettingsTab from './NotificationSettingsTab';
import GeneralSettingsTab from './GeneralSettingsTab';
import HomepageContentTab from './HomepageContentTab';
import BlockedDatesTab from './BlockedDatesTab';
import TgaIntegrationTab from './TgaIntegrationTab';
import ActivityTab from './ActivityTab';
import SunbedZonesTab from './SunbedZonesTab';
import ManualRoomBookingModal from './ManualRoomBookingModal';
import ManualSunbedBookingModal from './ManualSunbedBookingModal';
import ReservationSuccessModal from './ReservationSuccessModal';
import { adminAPI, productsAPI } from './services/api';
import './AdminPanel.css';

interface RowConfig {
  sunbedCount: number;
  price: number;
}

interface PricingConfig {
  sunbed: {
    price: number; // Backward compatibility için tutulur
    total: number;
    rowConfig: RowConfig[]; // Her sırada kaç şezlong ve fiyatı
  };
  lodgingBreakfast: {
    adultPrice: number;
    child0_6Price: number;
    child7_12Price: number;
  };
  breakfast: {
    adultPrice: number;
    child4_6Price: number;
    child7_12Price: number;
    capacity: number;
  };
  dinner: {
    adultPrice: number;
    child4_6Price: number;
    child7_12Price: number;
    capacity: number;
  };
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'activity' | 'sunbed' | 'breakfast' | 'dinner' | 'bookings' | 'revenue' | 'hotel-integration' | 'tga-integration' | 'rooms' | 'notifications' | 'general-settings' | 'homepage-content' | 'blocked-dates'>('dashboard');

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Manuel rezervasyon modal state
  const [showManualBookingModal, setShowManualBookingModal] = useState(false);
  const [showManualSunbedModal, setShowManualSunbedModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [reservationData, setReservationData] = useState<any>(null);

  // Kahvaltı Menü State
  const [breakfastMenuHTML, setBreakfastMenuHTML] = useState<string>(
    `<h3>☕ Kahvaltı Menümüz</h3>
<ul>
<li><strong>🧀 Kahvaltılık peynir çeşitleri</strong></li>
<li><strong>🥖 Taze fırın ürünleri</strong></li>
<li><strong>🍳 Sıcak yemekler</strong></li>
<li><strong>🥗 Taze sebze ve meyve</strong></li>
<li><strong>☕ Sınırsız çay &amp; kahve</strong></li>
<li><strong>🥞 Pancake &amp; Waffle</strong></li>
<li><strong>🍯 Organik bal &amp; reçel</strong></li>
<li><strong>🥤 Taze sıkılmış meyve suları</strong></li>
</ul>`
  );

  // Konaklama Kahvaltısı Menü State
  const [lodgingBreakfastMenuHTML, setLodgingBreakfastMenuHTML] = useState<string>(
    `<h3>🏨 Konaklama Kahvaltısı</h3>
<ul>
<li><strong>🧀 Kahvaltılık peynir ve zeytin çeşitleri</strong></li>
<li><strong>🥖 Taze fırın ürünleri</strong></li>
<li><strong>🍳 Menemen, yumurta çeşitleri</strong></li>
<li><strong>🥗 Taze sebze tabağı</strong></li>
<li><strong>☕ Sınırsız çay &amp; kahve</strong></li>
<li><strong>🍯 Bal, tereyağı, reçel</strong></li>
<li><strong>🥤 Taze sıkılmış meyve suyu</strong></li>
</ul>`
  );

  // Akşam Yemeği Menü State
  const [dinnerMenuHTML, setDinnerMenuHTML] = useState<string>(
    `<h3>🥗 Başlangıçlar</h3>
<ul>
<li>Mevsim Salata</li>
<li>Közlenmiş Patlıcan Salatası</li>
<li>Humus</li>
<li>Çoban Salata</li>
</ul>
<h3>🍲 Ana Yemekler</h3>
<ul>
<li>Izgara Somon</li>
<li>Dana Bonfile</li>
<li>Tavuk Şiş</li>
<li>Mantarlı Risotto (Vejetaryen)</li>
</ul>
<h3>🍝 Yan Yemekler</h3>
<ul>
<li>Patates Püresi</li>
<li>Közlenmiş Sebzeler</li>
<li>Pilav</li>
<li>Makarna</li>
</ul>
<h3>🍰 Tatlılar</h3>
<ul>
<li>Tiramisu</li>
<li>Sütlaç</li>
<li>Mevsim Meyveleri</li>
<li>Dondurma</li>
</ul>
<h3>🥤 İçecekler</h3>
<ul>
<li>Sınırsız Çay & Kahve</li>
<li>Meyve Suyu</li>
<li>Su</li>
</ul>`
  );

  // Şezlong Özellikleri State
  const [sunbedFeaturesHTML, setSunbedFeaturesHTML] = useState<string>(
    `<ul>
<li>🌞 Şemsiye dahil</li>
<li>🛌 Konforlu yalın ayak</li>
<li>🧸 Havlu servisi</li>
<li>🧳 Soyunma kabini erişimi</li>
<li>🚿 Duş imkanı</li>
<li>🍹 Beach bar yakınında</li>
</ul>`
  );

  // Fiyatlandırma State
  const [pricing, setPricing] = useState<PricingConfig>({
    sunbed: {
      price: 300,
      total: 72,
      rowConfig: [
        { sunbedCount: 12, price: 400 }, // Ön sıra - en pahalı
        { sunbedCount: 12, price: 350 },
        { sunbedCount: 12, price: 300 },
        { sunbedCount: 12, price: 300 },
        { sunbedCount: 12, price: 250 },
        { sunbedCount: 12, price: 200 }  // Arka sıra - en ucuz
      ]
    },
    lodgingBreakfast: {
      adultPrice: 0,
      child0_6Price: 0,
      child7_12Price: 0
    },
    breakfast: {
      adultPrice: 0,
      child4_6Price: 0,
      child7_12Price: 0,
      capacity: 100
    },
    dinner: {
      adultPrice: 0,
      child4_6Price: 0,
      child7_12Price: 0,
      capacity: 100
    }
  });

  // Rezervasyonlar State
  const [bookings, setBookings] = useState<any[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [activeUsers, setActiveUsers] = useState<{ total: number; byPage: Record<string, number> } | null>(null);
  const [sunbedConflicts, setSunbedConflicts] = useState<any[]>([]);
  const [missingReservations, setMissingReservations] = useState<any[]>([]);
  const [expandedRooms, setExpandedRooms] = useState<Set<number>>(new Set());
  const [bookingsFilter, setBookingsFilter] = useState({
    product_type: '',
    status: '',
    start_date: '',
    end_date: ''
  });
  
  // Takvim State
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  
  // Kahvaltı Takvim State
  const [selectedBreakfastDate, setSelectedBreakfastDate] = useState<string | null>(null);
  const [breakfastMonth, setBreakfastMonth] = useState<Date>(new Date());
  
  // Akşam Yemeği Takvim State
  const [selectedDinnerDate, setSelectedDinnerDate] = useState<string | null>(null);
  const [dinnerMonth, setDinnerMonth] = useState<Date>(new Date());
  const [dinnerWebEnabled, setDinnerWebEnabled] = useState<boolean>(true);
  // Akşam yemeği kapalı günler (0=Paz, 1=Pzt, 2=Sal, 3=Çar, 4=Per, 5=Cum, 6=Cmt)
  const [dinnerClosedDays, setDinnerClosedDays] = useState<number[]>([1]);

  // LocalStorage'dan veri yükle
  useEffect(() => {
    const savedBreakfastMenuHTML = localStorage.getItem('breakfastMenuHTML');
    const savedLodgingBreakfastMenuHTML = localStorage.getItem('lodgingBreakfastMenuHTML');
    const savedDinnerMenuHTML = localStorage.getItem('dinnerMenuHTML');
    const savedSunbedFeaturesHTML = localStorage.getItem('sunbedFeaturesHTML');
    const savedPricing = localStorage.getItem('pricing');
    if (savedBreakfastMenuHTML) {
      setBreakfastMenuHTML(savedBreakfastMenuHTML);
    }
    if (savedLodgingBreakfastMenuHTML) {
      setLodgingBreakfastMenuHTML(savedLodgingBreakfastMenuHTML);
    }
    if (savedDinnerMenuHTML) {
      setDinnerMenuHTML(savedDinnerMenuHTML);
    }
    if (savedSunbedFeaturesHTML) {
      setSunbedFeaturesHTML(savedSunbedFeaturesHTML);
    }
    if (savedPricing) {
      try {
        const loadedPricing = JSON.parse(savedPricing);
        // Migration logic - eski formatları yeni yapıya çevir
        if (loadedPricing.sunbed) {
          // Eğer rowConfig yoksa (en eski format)
          if (!loadedPricing.sunbed.rowConfig) {
            const rows = loadedPricing.sunbed.rows || 6;
            const perRow = loadedPricing.sunbed.perRow || 12;
            const defaultPrice = loadedPricing.sunbed.price || 300;
            loadedPricing.sunbed.rowConfig = Array(rows).fill(null).map(() => ({
              sunbedCount: perRow,
              price: defaultPrice
            }));
            loadedPricing.sunbed.total = rows * perRow;
          }
          // Eğer rowConfig number[] ise (bir önceki format)
          else if (Array.isArray(loadedPricing.sunbed.rowConfig) && 
                   typeof loadedPricing.sunbed.rowConfig[0] === 'number') {
            const defaultPrice = loadedPricing.sunbed.price || 300;
            loadedPricing.sunbed.rowConfig = loadedPricing.sunbed.rowConfig.map((count: number) => ({
              sunbedCount: count,
              price: defaultPrice
            }));
          }
        }
        // Capacity alanlarını ekle (backward compatibility)
        if (loadedPricing.breakfast && !loadedPricing.breakfast.capacity) {
          loadedPricing.breakfast.capacity = 100;
        }
        if (loadedPricing.dinner && !loadedPricing.dinner.capacity) {
          loadedPricing.dinner.capacity = 100;
        }
        // lodgingBreakfast yoksa ekle (backward compatibility)
        if (!loadedPricing.lodgingBreakfast) {
          loadedPricing.lodgingBreakfast = {
            adultPrice: 0,
            child0_6Price: 0,
            child7_12Price: 0
          };
        }
        setPricing(loadedPricing);
      } catch (e) {
        console.error('Pricing yüklenemedi:', e);
      }
    }
    // Bookings artık backend'den çekilecek
  }, []);

  // Backend'den güncel fiyatları çek (localStorage'daki eski/varsayılan değerleri override et)
  useEffect(() => {
    const loadPricingFromBackend = async () => {
      try {
        const [dinnerRes, breakfastRes, lodgingBfRes, dinnerPublicRes] = await Promise.all([
          productsAPI.getByType('dinner').catch(() => null),
          productsAPI.getByType('breakfast').catch(() => null),
          productsAPI.getPublicSetting('lodging_breakfast').catch(() => null),
          productsAPI.getPublicSetting('dinner_public').catch(() => null)
        ]);

        setPricing(prev => {
          const updated = { ...prev };

          // Akşam yemeği fiyatları
          if (dinnerRes?.data?.data?.content?.price_rules) {
            const dr = dinnerRes.data.data.content.price_rules;
            updated.dinner = {
              ...updated.dinner,
              adultPrice: dr.adult ?? updated.dinner.adultPrice,
              child4_6Price: dr.child_0_6 ?? updated.dinner.child4_6Price,
              child7_12Price: dr.child_7_12 ?? updated.dinner.child7_12Price,
              capacity: dinnerRes.data.data.content.capacity ?? updated.dinner.capacity
            };
          }

          // Günübirlik kahvaltı fiyatları
          if (breakfastRes?.data?.data?.content?.price_rules) {
            const br = breakfastRes.data.data.content.price_rules;
            updated.breakfast = {
              ...updated.breakfast,
              adultPrice: br.adult ?? updated.breakfast.adultPrice,
              child4_6Price: br.child_0_6 ?? updated.breakfast.child4_6Price,
              child7_12Price: br.child_7_12 ?? updated.breakfast.child7_12Price,
              capacity: breakfastRes.data.data.content.capacity ?? updated.breakfast.capacity
            };
          }

          // Konaklama kahvaltısı fiyatları
          if (lodgingBfRes?.data?.data?.value?.price_rules) {
            const lb = lodgingBfRes.data.data.value.price_rules;
            updated.lodgingBreakfast = {
              ...updated.lodgingBreakfast,
              adultPrice: lb.adult ?? updated.lodgingBreakfast.adultPrice,
              child0_6Price: lb.child_0_6 ?? updated.lodgingBreakfast.child0_6Price,
              child7_12Price: lb.child_7_12 ?? updated.lodgingBreakfast.child7_12Price
            };
          }

          // Menü HTML'lerini de güncelle
          if (dinnerRes?.data?.data?.content?.menu_html) {
            setDinnerMenuHTML(dinnerRes.data.data.content.menu_html);
          }
          if (breakfastRes?.data?.data?.content?.menu_html) {
            setBreakfastMenuHTML(breakfastRes.data.data.content.menu_html);
          }
          if (lodgingBfRes?.data?.data?.value?.menu_html) {
            setLodgingBreakfastMenuHTML(lodgingBfRes.data.data.value.menu_html);
          }

          // localStorage'ı da güncelle (senkronize tut)
          localStorage.setItem('pricing', JSON.stringify(updated));

          return updated;
        });

        const dinnerPublicValue = dinnerPublicRes?.data?.data?.value;
        if (dinnerPublicValue) {
          const normalizedDays: number[] = Array.isArray(dinnerPublicValue.closed_days)
            ? Array.from(new Set<number>(dinnerPublicValue.closed_days
                .map((d: any) => Number(d))
                .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)))
                .sort((a, b) => a - b)
            : [1];
          setDinnerWebEnabled(dinnerPublicValue.enabled !== false);
          setDinnerClosedDays(normalizedDays.length > 0 ? normalizedDays : [1]);
        }
      } catch (e) {
        console.error('Backend\'den fiyat çekme hatası:', e);
      }
    };

    loadPricingFromBackend();
  }, []);

  // Backend'den rezervasyonları çek
  const fetchBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      let url = '/admin/bookings?';
      if (bookingsFilter.product_type) url += `product_type=${bookingsFilter.product_type}&`;
      if (bookingsFilter.status) url += `status=${bookingsFilter.status}&`;
      if (bookingsFilter.start_date) url += `start_date=${bookingsFilter.start_date}&`;
      if (bookingsFilter.end_date) url += `end_date=${bookingsFilter.end_date}&`;

      const response = await adminAPI.get(url);
      if (response.data.success) {
        setBookings(response.data.data);
      }
    } catch (error) {
      console.error('Rezervasyonlar yüklenemedi:', error);
    } finally {
      setBookingsLoading(false);
    }
  }, [bookingsFilter]);

  // Dashboard veya Bookings sekmesi aktifken verileri çek
  useEffect(() => {
    if (activeTab === 'bookings' || activeTab === 'dashboard' || activeTab === 'revenue') {
      fetchBookings();
    }
  }, [activeTab, fetchBookings]);

  // Aktif kullanıcıları çek — dashboard açıkken 30sn'de bir güncelle
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    const fetchActiveUsers = () => {
      adminAPI.get('/admin/analytics/active-users')
        .then(r => { if (r.data.success) setActiveUsers(r.data.data); })
        .catch(() => {});
    };
    fetchActiveUsers();
    const iv = setInterval(fetchActiveUsers, 30_000);
    return () => clearInterval(iv);
  }, [activeTab]);

  // Şezlong çakışma uyarısı — aynı tarih+bölgede birden fazla geçerli rezervasyon
  // var mı kontrol et. Admin panel açıkken 60sn'de bir tazele (her sekmede gösterilir).
  const fetchConflicts = useCallback(() => {
    adminAPI.get('/admin/sunbed/conflicts')
      .then(r => { if (r.data.success) setSunbedConflicts(r.data.data.conflicts || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchConflicts();
    const iv = setInterval(fetchConflicts, 60_000);
    return () => clearInterval(iv);
  }, [fetchConflicts]);

  // AsistPro health-check: kayıp rezervasyon uyarısı — 4 saatte bir cron çalışır,
  // burada 15 dk'da bir son sonucu çekiyoruz.
  useEffect(() => {
    const fetchHealth = () => {
      adminAPI.get('/admin/asistpro/health')
        .then(r => {
          if (r.data.success) setMissingReservations(r.data.data?.missing || []);
        })
        .catch(() => {});
    };
    fetchHealth();
    const iv = setInterval(fetchHealth, 15 * 60_000);
    return () => clearInterval(iv);
  }, []);

  // Bir çakışmayı "incelendi / görmezden gel" olarak işaretle
  const dismissConflict = async (c: any) => {
    if (!window.confirm(
      `Bu çakışmayı "incelendi" olarak işaretleyip uyarıdan kaldıralım mı?\n\n` +
      `${c.zone_label || 'Bölge ' + c.zone_id} · ${c.date_str}\n\n` +
      `Not: Bu bölgeye sonradan YENİ bir rezervasyon daha eklenirse uyarı tekrar görünür.`
    )) return;
    try {
      await adminAPI.post('/admin/sunbed/conflicts/dismiss', {
        zone_id: c.zone_id,
        date: c.date_str,
        booking_ids: (c.bookings || []).map((b: any) => b.booking_id)
      });
      setSunbedConflicts(prev => prev.filter(
        x => !(x.zone_id === c.zone_id && x.date_str === c.date_str && x.signature === c.signature)
      ));
    } catch (e: any) {
      alert('İşlem başarısız: ' + (e.response?.data?.message || e.message));
    }
  };

  // Kahvaltı Menü İşlemleri
  const handleBreakfastMenuChange = (html: string) => {
    setBreakfastMenuHTML(html);
  };

  // Akşam Yemeği Menü İşlemleri
  const handleDinnerMenuChange = (html: string) => {
    setDinnerMenuHTML(html);
  };

  const saveDinnerPublicSettings = async (enabled: boolean, closedDays: number[]) => {
    try {
      await adminAPI.updateSettings('dinner_public', {
        enabled,
        closed_days: closedDays
      }, 'Akşam yemeği web satış durumu ve kapalı günler');

      // Açık diğer sekmelerin menüyü anlık güncellemesi için yayınla
      localStorage.setItem('dinnerPublicSettings', JSON.stringify({
        enabled,
        closed_days: closedDays,
        updated_at: Date.now()
      }));

      window.dispatchEvent(new CustomEvent('dinner-public-settings-changed', {
        detail: { enabled, closed_days: closedDays }
      }));
    } catch (e) {
      console.error('Akşam yemeği satış ayarları kaydedilemedi:', e);
      alert('Akşam yemeği satış ayarları kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  // Şezlong Özellikleri İşlemleri
  const handleSunbedFeaturesChange = (html: string) => {
    setSunbedFeaturesHTML(html);
  };

  // Fiyatlandırma İşlemleri
  const handlePricingUpdate = (category: keyof PricingConfig, field: string, value: number) => {
    const updated = {
      ...pricing,
      [category]: {
        ...pricing[category],
        [field]: value
      }
    };
    
    // Şezlong için toplam sayıyı otomatik hesapla
    if (category === 'sunbed' && field === 'rowConfig') {
      updated.sunbed.total = (value as any as number[]).reduce((sum, count) => sum + count, 0);
    }
    
    setPricing(updated);
  };

  // Şezlong sıra yapılandırmasını güncelle - sunbed sayısı
  const handleRowConfigChange = (rowIndex: number, field: 'sunbedCount' | 'price', value: number) => {
    const newRowConfig = [...pricing.sunbed.rowConfig];
    newRowConfig[rowIndex] = {
      ...newRowConfig[rowIndex],
      [field]: Math.max(0, value)
    };
    const total = newRowConfig.reduce((sum, row) => sum + row.sunbedCount, 0);
    
    setPricing({
      ...pricing,
      sunbed: {
        ...pricing.sunbed,
        rowConfig: newRowConfig,
        total
      }
    });
  };

  // Sıra ekle
  const handleAddRow = () => {
    const newRowConfig = [...pricing.sunbed.rowConfig, { sunbedCount: 12, price: 300 }];
    const total = newRowConfig.reduce((sum, row) => sum + row.sunbedCount, 0);
    
    setPricing({
      ...pricing,
      sunbed: {
        ...pricing.sunbed,
        rowConfig: newRowConfig,
        total
      }
    });
  };

  // Sıra sil
  const handleRemoveRow = (rowIndex: number) => {
    if (pricing.sunbed.rowConfig.length <= 1) {
      alert('En az 1 sıra olmalıdır!');
      return;
    }
    
    const newRowConfig = pricing.sunbed.rowConfig.filter((_, index) => index !== rowIndex);
    const total = newRowConfig.reduce((sum, row) => sum + row.sunbedCount, 0);
    
    setPricing({
      ...pricing,
      sunbed: {
        ...pricing.sunbed,
        rowConfig: newRowConfig,
        total
      }
    });
  };

  // Tüm Değişiklikleri Kaydet
  const handleSaveAll = async () => {
    try {
      // LocalStorage'a kaydet (backward compatibility)
      localStorage.setItem('breakfastMenuHTML', breakfastMenuHTML);
      localStorage.setItem('lodgingBreakfastMenuHTML', lodgingBreakfastMenuHTML);
      localStorage.setItem('dinnerMenuHTML', dinnerMenuHTML);
      localStorage.setItem('sunbedFeaturesHTML', sunbedFeaturesHTML);
      localStorage.setItem('pricing', JSON.stringify(pricing));

      // Backend'e konaklama kahvaltısı fiyatlarını kaydet (settings tablosuna)
      await adminAPI.updateSettings('lodging_breakfast', {
        price_rules: {
          adult: pricing.lodgingBreakfast.adultPrice,
          child_0_6: pricing.lodgingBreakfast.child0_6Price,
          child_7_12: pricing.lodgingBreakfast.child7_12Price
        },
        menu_html: lodgingBreakfastMenuHTML
      }, 'Konaklama kahvaltısı fiyatları ve içeriği');

      // Backend'e günübirlik kahvaltı fiyatlarını kaydet
      await adminAPI.updateProduct('breakfast', {
        content: {
          price_rules: {
            adult: pricing.breakfast.adultPrice,
            child_0_6: pricing.breakfast.child4_6Price,
            child_7_12: pricing.breakfast.child7_12Price
          },
          capacity: pricing.breakfast.capacity,
          menu_html: breakfastMenuHTML
        }
      });

      // Backend'e akşam yemeği fiyatlarını kaydet
      await adminAPI.updateProduct('dinner', {
        content: {
          price_rules: {
            adult: pricing.dinner.adultPrice,
            child_0_6: pricing.dinner.child4_6Price,
            child_7_12: pricing.dinner.child7_12Price
          },
          capacity: pricing.dinner.capacity,
          menu_html: dinnerMenuHTML
        }
      });

      // Akşam yemeği web satış ayarları
      await adminAPI.updateSettings('dinner_public', {
        enabled: dinnerWebEnabled,
        closed_days: dinnerClosedDays
      }, 'Akşam yemeği web satış durumu ve kapalı günler');

      alert('✅ Tüm değişiklikler başarıyla kaydedildi!');
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('⚠️ Yerel kayıt yapıldı, ancak sunucuya kaydetme başarısız oldu.');
    }
  };

  // Manuel Rezervasyon Fonksiyonu
  const handleManualReservation = (sunbedId: number, dateStr: string, isCurrentlyBooked: boolean) => {
    if (isCurrentlyBooked) {
      // Rezervasyonu iptal et
      const confirmCancel = window.confirm(`Şezlong ${sunbedId} rezervasyonunu iptal etmek istiyor musunuz?`);
      if (!confirmCancel) return;

      const updatedBookings = bookings.filter(booking => {
        if (booking.type === 'sunbed' && booking.date === dateStr) {
          const ids = booking.sunbedIds || [];
          return !ids.includes(sunbedId);
        }
        return true;
      });

      setBookings(updatedBookings);
      localStorage.setItem('bookings', JSON.stringify(updatedBookings));
      alert('✅ Rezervasyon iptal edildi!');
    } else {
      // Yeni rezervasyon yap
      const customerName = prompt('Müşteri Adı Soyadı:');
      if (!customerName || !customerName.trim()) {
        alert('Müşteri adı gereklidir!');
        return;
      }

      const customerPhone = prompt('Müşteri Telefonu:');
      if (!customerPhone || !customerPhone.trim()) {
        alert('Telefon numarası gereklidir!');
        return;
      }

      // Yeni rezervasyon oluştur
      const newBooking = {
        id: Date.now(),
        type: 'sunbed',
        date: dateStr,
        sunbedIds: [sunbedId],
        sunbedCount: 1,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        status: 'completed',
        paymentMethod: 'manual',
        createdAt: new Date().toISOString(),
        total: 0 // Manuel rezervasyon, fiyat önemli değil
      };

      const updatedBookings = [...bookings, newBooking];
      setBookings(updatedBookings);
      localStorage.setItem('bookings', JSON.stringify(updatedBookings));
      alert('✅ Manuel rezervasyon başarıyla oluşturuldu!');
    }
  };

  // Manuel Kahvaltı Rezervasyonu
  const handleManualBreakfastReservation = (dateStr: string) => {
    const customerName = prompt('Müşteri Adı Soyadı:');
    if (!customerName || !customerName.trim()) {
      alert('Müşteri adı gereklidir!');
      return;
    }

    const customerPhone = prompt('Müşteri Telefonu:');
    if (!customerPhone || !customerPhone.trim()) {
      alert('Telefon numarası gereklidir!');
      return;
    }

    const adultsStr = prompt('Yetişkin Sayısı:', '0');
    const adults = parseInt(adultsStr || '0') || 0;

    const children4_6Str = prompt('4-6 Yaş Çocuk Sayısı:', '0');
    const children4_6 = parseInt(children4_6Str || '0') || 0;

    const children7_12Str = prompt('7-12 Yaş Çocuk Sayısı:', '0');
    const children7_12 = parseInt(children7_12Str || '0') || 0;

    const totalPeople = adults + children4_6 + children7_12;

    if (totalPeople === 0) {
      alert('En az 1 kişi girmelisiniz!');
      return;
    }

    // Kapasite kontrolü
    const currentBooked = getBookedBreakfastCount(dateStr);
    const available = pricing.breakfast.capacity - currentBooked;

    if (totalPeople > available) {
      alert(`Yetersiz kapasite!\nMüsait: ${available} kişi\nTalep: ${totalPeople} kişi`);
      return;
    }

    // Yeni rezervasyon oluştur
    const newBooking = {
      id: Date.now(),
      type: 'breakfast',
      date: dateStr,
      adults,
      children4_6,
      children7_12,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      status: 'completed',
      paymentMethod: 'manual',
      createdAt: new Date().toISOString(),
      total: 0
    };

    const updatedBookings = [...bookings, newBooking];
    setBookings(updatedBookings);
    localStorage.setItem('bookings', JSON.stringify(updatedBookings));
    alert(`✅ Manuel rezervasyon başarıyla oluşturuldu!\n${totalPeople} kişi için kahvaltı rezervasyonu yapıldı.`);
  };

  // Manuel Akşam Yemeği Rezervasyonu
  const handleManualDinnerReservation = (dateStr: string) => {
    const customerName = prompt('Müşteri Adı Soyadı:');
    if (!customerName || !customerName.trim()) {
      alert('Müşteri adı gereklidir!');
      return;
    }

    const customerPhone = prompt('Müşteri Telefonu:');
    if (!customerPhone || !customerPhone.trim()) {
      alert('Telefon numarası gereklidir!');
      return;
    }

    const adultsStr = prompt('Yetişkin Sayısı:', '0');
    const adults = parseInt(adultsStr || '0') || 0;

    const children4_6Str = prompt('4-6 Yaş Çocuk Sayısı:', '0');
    const children4_6 = parseInt(children4_6Str || '0') || 0;

    const children7_12Str = prompt('7-12 Yaş Çocuk Sayısı:', '0');
    const children7_12 = parseInt(children7_12Str || '0') || 0;

    const totalPeople = adults + children4_6 + children7_12;

    if (totalPeople === 0) {
      alert('En az 1 kişi girmelisiniz!');
      return;
    }

    // Kapasite kontrolü
    const currentBooked = getBookedDinnerCount(dateStr);
    const available = pricing.dinner.capacity - currentBooked;

    if (totalPeople > available) {
      alert(`Yetersiz kapasite!\nMüsait: ${available} kişi\nTalep: ${totalPeople} kişi`);
      return;
    }

    // Yeni rezervasyon oluştur
    const newBooking = {
      id: Date.now(),
      type: 'dinner',
      date: dateStr,
      adults,
      children4_6,
      children7_12,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      status: 'completed',
      paymentMethod: 'manual',
      createdAt: new Date().toISOString(),
      total: 0
    };

    const updatedBookings = [...bookings, newBooking];
    setBookings(updatedBookings);
    localStorage.setItem('bookings', JSON.stringify(updatedBookings));
    alert(`✅ Manuel rezervasyon başarıyla oluşturuldu!\n${totalPeople} kişi için akşam yemeği rezervasyonu yapıldı.`);
  };

  // Takvim Helper Fonksiyonları
  const getTotalSunbeds = () => {
    return pricing.sunbed.rowConfig.reduce((sum, row) => sum + row.sunbedCount, 0);
  };

  const getBookingsForDate = (date: string, type?: string) => {
    return bookings.filter(booking => {
      // booking_date'i normalize et (sadece YYYY-MM-DD al)
      let bookingDateStr = '';
      if (booking.booking_date) {
        bookingDateStr = booking.booking_date.split('T')[0];
      }
      
      const productType = booking.product_type || booking.type;
      const isPaid = booking.payment_status === 'paid' || booking.status === 'completed' || booking.status === 'confirmed';
      
      // Debug log
      if (productType === type || (!type && productType === 'sunbed')) {
        console.log('Booking:', bookingDateStr, 'Target:', date, 'Match:', bookingDateStr === date, 'Paid:', isPaid);
      }
      
      return (type ? productType === type : productType === 'sunbed') && 
        bookingDateStr === date && 
        isPaid;
    });
  };

  const getBookedSunbedsCount = (date: string) => {
    const dateBookings = getBookingsForDate(date, 'sunbed');
    return dateBookings.reduce((sum, booking) => {
      // items içinde sunbeds array varsa onu say, yoksa sunbedCount kullan
      if (booking.items?.sunbeds) {
        return sum + booking.items.sunbeds.length;
      }
      return sum + (booking.sunbedCount || 1);
    }, 0);
  };
  
  const getBookedBreakfastCount = (date: string) => {
    const dateBookings = getBookingsForDate(date, 'breakfast');
    return dateBookings.reduce((sum, booking) => {
      // customer_info veya items içinden misafir sayısını al
      const guestCount = booking.items?.guestCount || 
                         booking.customer_info?.guests || 
                         booking.adults || 
                         1;
      return sum + guestCount;
    }, 0);
  };
  
  const getBookedDinnerCount = (date: string) => {
    const dateBookings = getBookingsForDate(date, 'dinner');
    return dateBookings.reduce((sum, booking) => {
      const guestCount = booking.items?.guestCount || 
                         booking.customer_info?.guests || 
                         booking.adults || 
                         1;
      return sum + guestCount;
    }, 0);
  };

  // Dashboard İstatistik Fonksiyonları - SATIŞ TARİHİNE GÖRE (created_at)
  const getSalesStatsForPeriod = (startDate: Date, endDate: Date) => {
    const stats = {
      room: 0,
      sunbed: 0,
      breakfast: 0,
      dinner: 0
    };

    // Satış tarihi = created_at (rezervasyonun yapıldığı tarih)
    bookings.forEach(booking => {
      const productType = booking.product_type || booking.type;
      const isPaid = booking.payment_status === 'paid' || booking.status === 'completed' || booking.status === 'confirmed';
      if (!isPaid) return;
      
      // created_at tarihini kullan (satış tarihi)
      const createdAtStr = booking.created_at;
      if (!createdAtStr) return;
      
      const createdAt = new Date(createdAtStr);
      createdAt.setHours(0, 0, 0, 0);
      
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      if (createdAt >= start && createdAt <= end) {
        if (productType === 'room') {
          stats.room += 1;
        } else if (productType === 'sunbed') {
          // Şezlong sayısını hesapla
          const sunbedCount = booking.items?.sunbeds?.length || booking.items?.sunbedCount || 1;
          stats.sunbed += sunbedCount;
        } else if (productType === 'breakfast') {
          const personCount = booking.items?.totalPersons || booking.items?.guestCount || 1;
          stats.breakfast += personCount;
        } else if (productType === 'dinner') {
          const personCount = booking.items?.totalPersons || booking.items?.guestCount || 1;
          stats.dinner += personCount;
        }
      }
    });

    return stats;
  };

  const getDailyStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    console.log('getDailyStats - Today:', todayStr, 'Bookings count:', bookings.length);
    return getSalesStatsForPeriod(today, today);
  };

  const getWeeklyStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStart = new Date(today);
    // Pazar günü (0) ise 6 gün geriye, diğer günler için (getDay() - 1) gün geriye
    const daysToMonday = today.getDay() === 0 ? 6 : today.getDay() - 1;
    weekStart.setDate(today.getDate() - daysToMonday); // Pazartesi
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Pazar
    return getSalesStatsForPeriod(weekStart, weekEnd);
  };

  const getMonthlyStats = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return getSalesStatsForPeriod(monthStart, monthEnd);
  };

  // Kazanç Hesaplama Fonksiyonları - SATIŞ TARİHİNE GÖRE (created_at)
  const getRevenueForPeriod = (startDate: Date, endDate: Date, includeAll: boolean = false) => {
    const revenue = {
      sunbed: 0,
      breakfast: 0,
      dinner: 0,
      room: 0,
      total: 0
    };

    const filteredBookings = bookings.filter(booking => {
      // İptal edilenleri / iadeli olanları kazançtan hariç tut
      if (booking.status === 'cancelled') return false;
      if (booking.payment_status === 'refunded' || booking.payment_status === 'failed') return false;

      // Ödeme yapılmış olanları say
      const isPaid = booking.payment_status === 'paid' || booking.status === 'completed' || booking.status === 'confirmed';
      if (!isPaid) return false;
      
      // includeAll ise tarih filtresi yapma (TOPLAM için)
      if (includeAll) return true;
      
      // Satış tarihi = created_at (rezervasyonun yapıldığı tarih)
      const createdAtStr = booking.created_at;
      if (!createdAtStr) return false;
      
      const createdAt = new Date(createdAtStr);
      createdAt.setHours(0, 0, 0, 0);
      
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      return createdAt >= start && createdAt <= end;
    });

    filteredBookings.forEach(booking => {
      // total_price string olarak gelebilir, number'a çevir
      const rawAmount = booking.total_price || booking.total || 0;
      const amount = typeof rawAmount === 'string' ? parseFloat(rawAmount) : rawAmount;
      const productType = booking.product_type || booking.type;
      
      if (productType === 'sunbed') {
        revenue.sunbed += amount;
      } else if (productType === 'breakfast') {
        revenue.breakfast += amount;
      } else if (productType === 'dinner') {
        revenue.dinner += amount;
      } else if (productType === 'room') {
        revenue.room += amount;
      }
      revenue.total += amount;
    });

    return revenue;
  };

  const getDailyRevenue = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    return getRevenueForPeriod(today, endOfDay);
  };

  const getWeeklyRevenue = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStart = new Date(today);
    // Pazar günü (0) ise 6 gün geriye, diğer günler için (getDay() - 1) gün geriye
    const daysToMonday = today.getDay() === 0 ? 6 : today.getDay() - 1;
    weekStart.setDate(today.getDate() - daysToMonday); // Pazartesi
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return getRevenueForPeriod(weekStart, weekEnd);
  };

  const getMonthlyRevenue = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);
    return getRevenueForPeriod(monthStart, monthEnd);
  };

  const getTotalRevenue = () => {
    // Tüm zamanların toplamı için includeAll=true kullan
    const now = new Date();
    return getRevenueForPeriod(now, now, true);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Pazartesi = 0, Pazar = 6
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const formatDateForBooking = (year: number, month: number, day: number) => {
    const date = new Date(year, month, day);
    return date.toISOString().split('T')[0];
  };

  const changeMonth = (offset: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };
  const handleLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    window.location.reload();
  };

  return (
    <div className="admin-panel">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button 
          className="hamburger-btn" 
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          aria-label="Toggle Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <h2>🏨 Villa Polikne</h2>
        <button className="mobile-logout" onClick={handleLogout}>
          🚪
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <h2>🏨 Villa Polikne</h2>
          <span className="admin-badge">Admin Panel</span>
        </div>
        
        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-title">Genel</span>
            <button 
              className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('dashboard');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-text">Dashboard</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('activity');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">📈</span>
              <span className="nav-text">Aktivite</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'bookings' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('bookings');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">📋</span>
              <span className="nav-text">Rezervasyonlar</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'revenue' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('revenue');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">💰</span>
              <span className="nav-text">Kazançlar</span>
            </button>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Otel</span>
            <button 
              className={`nav-item ${activeTab === 'rooms' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('rooms');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🏠</span>
              <span className="nav-text">Odalar Yönetimi</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'hotel-integration' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('hotel-integration');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🔗</span>
              <span className="nav-text">Otel Entegrasyonu</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'tga-integration' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('tga-integration');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🛰️</span>
              <span className="nav-text">TGA Entegrasyon</span>
            </button>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Hizmetler</span>
            <button 
              className={`nav-item ${activeTab === 'sunbed' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('sunbed');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🏖️</span>
              <span className="nav-text">Şezlong</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'breakfast' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('breakfast');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🍳</span>
              <span className="nav-text">Kahvaltı</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'dinner' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('dinner');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🍽️</span>
              <span className="nav-text">Akşam Yemeği</span>
            </button>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Ayarlar</span>
            <button 
              className={`nav-item ${activeTab === 'general-settings' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('general-settings');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-text">Genel Ayarlar</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'homepage-content' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('homepage-content');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🏠</span>
              <span className="nav-text">Ana Sayfa İçerikleri</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('notifications');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">📧</span>
              <span className="nav-text">Bildirim Ayarları</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'blocked-dates' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('blocked-dates');
                setIsMobileSidebarOpen(false);
              }}
            >
              <span className="nav-icon">🚫</span>
              <span className="nav-text">Kapalı Tarihler</span>
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="btn-logout-sidebar" onClick={handleLogout}>
            🚪 Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          {/* 🚨 AsistPro Kayıp Rezervasyon Uyarısı */}
          {missingReservations.length > 0 && (
            <div className="sunbed-conflict-alert" style={{ borderLeftColor: '#e74c3c', backgroundColor: '#fff5f5' }}>
              <div className="sca-header">
                <span className="sca-icon">🚨</span>
                <span className="sca-title">
                  AsistPro'da {missingReservations.length} rezervasyon KAYIP!
                </span>
              </div>
              <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#c0392b' }}>
                Aşağıdaki ödeme alınmış rezervasyonlar AsistPro panelinde bulunmuyor. Müşteri geldiğinde oda hazır olmayabilir — acilen kontrol edin!
              </p>
              <div className="sca-list">
                {missingReservations.map((r: any, i: number) => (
                  <div key={i} className="sca-item" style={{ borderLeftColor: '#e74c3c' }}>
                    <div className="sca-item-header">
                      <span className="sca-date">📅 {r.booking_date ? new Date(r.booking_date).toLocaleDateString('tr-TR') : '-'}</span>
                      <span className="sca-badge" style={{ background: '#e74c3c' }}>KAYIP</span>
                    </div>
                    <div className="sca-bookings">
                      <div className="sca-booking">
                        <span className="sca-source">🌐</span>
                        <span className="sca-customer">{r.customer_name}</span>
                        {r.customer_email && <span style={{ fontSize: '0.75rem', color: '#666' }}> · {r.customer_email}</span>}
                        <span className="sca-amount"> — {Number(r.total_price).toLocaleString('tr-TR')} TL</span>
                        <code className="sca-code">{r.reservation_id}</code>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ⚠️ Şezlong Çakışma Uyarısı — aynı tarih+bölgede 2+ geçerli rezervasyon */}
          {sunbedConflicts.length > 0 && (
            <div className="sunbed-conflict-alert">
              <div className="sca-header">
                <span className="sca-icon">⚠️</span>
                <strong>
                  DİKKAT: {sunbedConflicts.length} adet çakışan şezlong rezervasyonu var!
                </strong>
                {activeTab !== 'bookings' && (
                  <button
                    className="sca-goto-btn"
                    onClick={() => setActiveTab('bookings')}
                  >
                    Rezervasyonları Gör →
                  </button>
                )}
              </div>
              <div className="sca-list">
                {sunbedConflicts.map((c: any, i: number) => (
                  <div key={i} className="sca-item">
                    <div className="sca-item-head">
                      <div className="sca-item-title">
                        📅 {new Date(c.booking_date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        {' · '}
                        <span className="sca-zone">{c.zone_label || `Bölge ${c.zone_id}`}</span>
                        {' · '}
                        <span className="sca-count">{c.booking_count} rezervasyon</span>
                      </div>
                      <button
                        className="sca-dismiss-btn"
                        title="Bu çakışmayı incelendi olarak işaretle ve uyarıdan kaldır"
                        onClick={() => dismissConflict(c)}
                      >
                        ✓ İncelendi
                      </button>
                    </div>
                    <div className="sca-bookings">
                      {(c.bookings || []).map((b: any, j: number) => (
                        <span key={j} className="sca-booking">
                          {b.created_by === 'admin' ? '🛠️' : '🌐'} {b.customer_name}
                          {b.customer_phone ? ` (${b.customer_phone})` : ''}
                          {' — '}
                          {Number(b.total_price).toLocaleString('tr-TR')} TL
                          {' · '}
                          <code>{b.reservation_id}</code>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="tab-content">
              <h2>📊 Dashboard</h2>

              {/* Aktif Kullanıcılar */}
              <div style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', borderRadius: '12px', padding: '1rem 1.5rem', color: 'white', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '2rem' }}>👁️</span>
                  <div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>ŞU AN SİTEDE</div>
                    <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
                      {activeUsers === null ? '—' : activeUsers.total}
                      <span style={{ fontSize: '1rem', fontWeight: 400, marginLeft: '0.4rem' }}>ziyaretçi</span>
                    </div>
                  </div>
                </div>
                {activeUsers && activeUsers.total > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {Object.entries(activeUsers.byPage).map(([page, count]) => {
                      const labels: Record<string, string> = { home: '🏠 Anasayfa', sunbed: '🏖️ Şezlong', rooms: '🏨 Odalar', breakfast: '🍳 Kahvaltı', dinner: '🍽️ Akşam', reservation: '📋 Rezervasyon', 'our-rooms': '🛏️ Odalarımız', 'reservation-lookup': '🔍 Sorgulama' };
                      return (
                        <span key={page} style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '0.2rem 0.75rem', fontSize: '0.85rem' }}>
                          {labels[page] || page} <strong>{count}</strong>
                        </span>
                      );
                    })}
                  </div>
                )}
                {activeUsers && activeUsers.total === 0 && (
                  <span style={{ opacity: 0.75, fontSize: '0.9rem' }}>Şu an aktif ziyaretçi yok</span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
                {/* Günlük İstatistikler */}
                {(() => {
                  const dailyStats = getDailyStats();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', opacity: 0.9 }}>📅 Günlük</h3>
                      <div style={{ fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>� Oda</span>
                          <strong style={{ fontSize: '1.2rem' }}>{dailyStats.room}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🏖️ Şezlong</span>
                          <strong style={{ fontSize: '1.2rem' }}>{dailyStats.sunbed}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🍳 Kahvaltı</span>
                          <strong style={{ fontSize: '1.2rem' }}>{dailyStats.breakfast}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🍽️ Akşam Yemeği</span>
                          <strong style={{ fontSize: '1.2rem' }}>{dailyStats.dinner}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Haftalık İstatistikler */}
                {(() => {
                  const weeklyStats = getWeeklyStats();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', opacity: 0.9 }}>📆 Haftalık</h3>
                      <div style={{ fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🏨 Oda</span>
                          <strong style={{ fontSize: '1.2rem' }}>{weeklyStats.room}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🏖️ Şezlong</span>
                          <strong style={{ fontSize: '1.2rem' }}>{weeklyStats.sunbed}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🍳 Kahvaltı</span>
                          <strong style={{ fontSize: '1.2rem' }}>{weeklyStats.breakfast}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🍽️ Akşam Yemeği</span>
                          <strong style={{ fontSize: '1.2rem' }}>{weeklyStats.dinner}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Aylık İstatistikler */}
                {(() => {
                  const monthlyStats = getMonthlyStats();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', opacity: 0.9 }}>📊 Aylık</h3>
                      <div style={{ fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>� Oda</span>
                          <strong style={{ fontSize: '1.2rem' }}>{monthlyStats.room}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>�🏖️ Şezlong</span>
                          <strong style={{ fontSize: '1.2rem' }}>{monthlyStats.sunbed}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🍳 Kahvaltı</span>
                          <strong style={{ fontSize: '1.2rem' }}>{monthlyStats.breakfast}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '6px' }}>
                          <span>🍽️ Akşam Yemeği</span>
                          <strong style={{ fontSize: '1.2rem' }}>{monthlyStats.dinner}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div style={{ 
                padding: '1.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #dee2e6'
              }}>
                <h3 style={{ marginTop: 0 }}>ℹ️ Bilgilendirme</h3>
                <ul style={{ marginBottom: 0, lineHeight: '1.8' }}>
                  <li><strong>Günlük:</strong> Bugünün rezervasyon sayıları</li>
                  <li><strong>Haftalık:</strong> Bu haftanın (Pazartesi-Pazar) toplam rezervasyon sayıları</li>
                  <li><strong>Aylık:</strong> Bu ayın toplam rezervasyon sayıları</li>
                </ul>
              </div>
            </div>
          )}
          
          {/* Activity Tab */}
          {activeTab === 'activity' && <ActivityTab />}
          
          {/* Şezlong */}
          {/* Şezlong - Bölge Bazlı */}
          {activeTab === 'sunbed' && (
            <SunbedZonesTab
              sunbedFeaturesHTML={sunbedFeaturesHTML}
              setSunbedFeaturesHTML={setSunbedFeaturesHTML}
            />
          )}

          {/* Kahvaltı */}
          {activeTab === 'breakfast' && (
            <div className="tab-content">
              <h2>🍳 Kahvaltı Yönetimi</h2>
              
              {/* Konaklama Kahvaltısı Bölümü */}
              <div className="pricing-section" style={{ backgroundColor: '#e8f5e9', border: '2px solid #4caf50', marginBottom: '30px' }}>
                <h3 style={{ color: '#2e7d32' }}>🏨 Konaklama Kahvaltısı</h3>
                <p className="info-text" style={{ backgroundColor: '#c8e6c9', borderColor: '#66bb6a' }}>
                  ℹ️ Bu kahvaltı, <strong>konaklayan misafirlere</strong> sunulan kahvaltıdır. Rezervasyon sırasında "kahvaltı dahil" seçeneği için bu fiyatlar geçerlidir.
                </p>
                
                <div className="pricing-grid" style={{ marginTop: '15px' }}>
                  <div className="pricing-item">
                    <label>Yetişkin Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.lodgingBreakfast.adultPrice}
                      onChange={(e) => handlePricingUpdate('lodgingBreakfast', 'adultPrice', parseFloat(e.target.value) || 0)}
                      className="input-field"
                      min="0"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>0-6 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.lodgingBreakfast.child0_6Price}
                      onChange={(e) => handlePricingUpdate('lodgingBreakfast', 'child0_6Price', parseFloat(e.target.value) || 0)}
                      className="input-field"
                      min="0"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>7-12 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.lodgingBreakfast.child7_12Price}
                      onChange={(e) => handlePricingUpdate('lodgingBreakfast', 'child7_12Price', parseFloat(e.target.value) || 0)}
                      className="input-field"
                      min="0"
                    />
                  </div>
                </div>

                <div className="menu-section" style={{ marginTop: '20px' }}>
                  <h4>Konaklama Kahvaltısı İçeriği</h4>
                  <div className="rich-editor-container">
                    <RichTextEditor
                      value={lodgingBreakfastMenuHTML}
                      onChange={setLodgingBreakfastMenuHTML}
                    />
                  </div>
                  <div className="menu-preview" style={{ marginTop: '15px' }}>
                    <h5>📋 Önizleme</h5>
                    <div 
                      className="menu-preview-content"
                      dangerouslySetInnerHTML={{ __html: lodgingBreakfastMenuHTML }}
                    />
                  </div>
                </div>
              </div>

              {/* Günübirlik Kahvaltı Bölümü */}
              <div className="pricing-section" style={{ backgroundColor: '#fff3e0', border: '2px solid #ff9800' }}>
                <h3 style={{ color: '#e65100' }}>☕ Günübirlik Kahvaltı (Hafta Sonu)</h3>
                <p className="info-text" style={{ backgroundColor: '#ffe0b2', borderColor: '#ffb74d' }}>
                  ℹ️ Bu kahvaltı, <strong>konaklama yapmayan</strong> misafirlere hafta sonları ayrıca satılan kahvaltıdır.
                </p>
              </div>
              
              <div className="pricing-section" style={{ marginTop: '20px' }}>
                <h3>Salon Kapasitesi</h3>
                <div className="pricing-item" style={{ maxWidth: '300px' }}>
                  <label>Maksimum Kapasite (Kişi)</label>
                  <input
                    type="number"
                    value={pricing.breakfast.capacity}
                    onChange={(e) => handlePricingUpdate('breakfast', 'capacity', parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="0"
                  />
                </div>
              </div>
              
              {/* Rezervasyon Takvimi */}
              <div className="menu-section" style={{ marginTop: '30px' }}>
                <h3>📅 Rezervasyon Takvimi</h3>
                <p className="info-text">
                  ℹ️ Her gün için rezerve edilen kahvaltı sayısını görün. Detaylar için tarihe tıklayın.
                </p>
                
                <div style={{ marginTop: '1rem' }}>
                  {/* Takvim Navigasyon */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <button onClick={() => setBreakfastMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() - 1); return d; })} className="btn-add" style={{ padding: '0.5rem 1rem' }}>
                      ◀ Önceki
                    </button>
                    <h4 style={{ margin: 0, fontSize: '1rem' }}>
                      {breakfastMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                      {' - '}
                      {(() => {
                        const nextMonth = new Date(breakfastMonth);
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        return nextMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
                      })()}
                    </h4>
                    <button onClick={() => setBreakfastMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() + 1); return d; })} className="btn-add" style={{ padding: '0.5rem 1rem' }}>
                      Sonraki ▶
                    </button>
                  </div>

                  {/* İki Takvim Yan Yana */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {[breakfastMonth, (() => { const next = new Date(breakfastMonth); next.setMonth(next.getMonth() + 1); return next; })()].map((monthDate, monthIndex) => {
                      const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(monthDate);
                      
                      return (
                        <div key={monthIndex}>
                          <h5 style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.95rem', color: '#495057' }}>
                            {monthDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                          </h5>
                          
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(7, 1fr)', 
                            gap: '0.3rem'
                          }}>
                            {/* Gün Başlıkları */}
                            {['P', 'S', 'Ç', 'P', 'C', 'C', 'P'].map((day, idx) => (
                              <div key={idx} style={{ 
                                textAlign: 'center', 
                                fontWeight: 'bold', 
                                fontSize: '0.7rem',
                                padding: '0.3rem',
                                color: '#6c757d',
                                backgroundColor: idx >= 5 ? '#e8f5e9' : 'transparent'
                              }}>
                                {day}
                              </div>
                            ))}

                            {/* Boş hücreler */}
                            {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                              <div key={`empty-${i}`} />
                            ))}

                            {/* Günler */}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                              const day = i + 1;
                              const dateStr = formatDateForBooking(year, month, day);
                              const bookedCount = getBookedBreakfastCount(dateStr);
                              const totalCount = pricing.breakfast.capacity;
                              const isSelected = selectedBreakfastDate === dateStr;
                              const isToday = new Date().toISOString().split('T')[0] === dateStr;
                              const dayOfWeek = new Date(year, month, day).getDay();
                              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                              
                              return (
                                <div
                                  key={day}
                                  onClick={() => setSelectedBreakfastDate(dateStr)}
                                  style={{
                                    border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    padding: '0.3rem',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isToday ? '#e7f3ff' : (bookedCount > 0 ? '#fff3cd' : (isWeekend ? '#e8f5e9' : '#f8f9fa')),
                                    transition: 'all 0.2s',
                                    minHeight: '45px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    fontSize: '0.8rem',
                                    position: 'relative'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)'}
                                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                                >
                                  <div style={{ fontWeight: '600', width: '100%', textAlign: 'center' }}>
                                    {day}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.65rem', 
                                    color: bookedCount > 0 ? '#856404' : '#6c757d', 
                                    fontWeight: '600',
                                    alignSelf: 'flex-end'
                                  }}>
                                    {bookedCount}/{totalCount}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Seçili Tarih Detayları */}
                  {selectedBreakfastDate && (
                    <div style={{ 
                      marginTop: '1.5rem', 
                      padding: '1rem', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #dee2e6'
                    }}>
                      <h4 style={{ marginBottom: '1rem' }}>
                        {new Date(selectedBreakfastDate + 'T00:00:00').toLocaleDateString('tr-TR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })} - Kahvaltı Rezervasyonları
                      </h4>
                      
                      {(() => {
                        const dayBookings = getBookingsForDate(selectedBreakfastDate, 'breakfast');
                        
                        if (dayBookings.length === 0) {
                          return (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                              Bu tarih için rezervasyon bulunmamaktadır.
                            </p>
                          );
                        }
                        
                        return (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ 
                              width: '100%', 
                              borderCollapse: 'collapse',
                              backgroundColor: '#fff'
                            }}>
                              <thead>
                                <tr style={{ backgroundColor: '#e9ecef' }}>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                                    Ad Soyad
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                                    Telefon
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Yetişkin
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    4-6 Yaş
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    7-12 Yaş
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Toplam
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayBookings.map((booking, index) => (
                                  <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '0.75rem' }}>
                                      {booking.customerName || 'Belirtilmemiş'}
                                    </td>
                                    <td style={{ padding: '0.75rem' }}>
                                      {booking.customerPhone || 'Belirtilmemiş'}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                      {booking.adults || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                      {booking.children4_6 || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                      {booking.children7_12 || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600' }}>
                                      {(booking.adults || 0) + (booking.children4_6 || 0) + (booking.children7_12 || 0)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div style={{ marginTop: '1rem', textAlign: 'right', fontWeight: '600', fontSize: '1rem' }}>
                              Toplam: {getBookedBreakfastCount(selectedBreakfastDate)} kişi
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Manuel Rezervasyon */}
              <div className="menu-section" style={{ marginTop: '30px' }}>
                <h3>🎯 Manuel Rezervasyon</h3>
                <p className="info-text">
                  ℹ️ Kapıdan gelen müşteriler için buradan kahvaltı rezervasyonu yapabilirsiniz.
                </p>
                
                <div style={{ marginTop: '1.5rem' }}>
                  {/* Tarih Seçimi */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      📅 Tarih Seçin:
                    </label>
                    <input
                      type="date"
                      value={selectedBreakfastDate || ''}
                      onChange={(e) => setSelectedBreakfastDate(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {selectedBreakfastDate && (
                    <>
                      {/* Kapasite Bilgisi */}
                      {(() => {
                        const bookedCount = getBookedBreakfastCount(selectedBreakfastDate);
                        const totalCapacity = pricing.breakfast.capacity;
                        const available = totalCapacity - bookedCount;
                        const percentage = (bookedCount / totalCapacity) * 100;
                        
                        return (
                          <div style={{
                            border: '2px solid #dee2e6',
                            borderRadius: '8px',
                            padding: '1.5rem',
                            backgroundColor: '#fff',
                            marginBottom: '1rem'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                              <div>
                                <h4 style={{ margin: 0, fontSize: '1.1rem' }}>Kapasite Durumu</h4>
                                <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                  {new Date(selectedBreakfastDate + 'T00:00:00').toLocaleDateString('tr-TR', { 
                                    day: 'numeric', 
                                    month: 'long', 
                                    year: 'numeric' 
                                  })}
                                </p>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: available > 0 ? '#28a745' : '#dc3545' }}>
                                  {available}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>kişi müsait</div>
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: '0.5rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '0.3rem' }}>
                                <span>Dolu: {bookedCount} kişi</span>
                                <span>Toplam: {totalCapacity} kişi</span>
                              </div>
                              <div style={{ 
                                width: '100%', 
                                height: '20px', 
                                backgroundColor: '#e9ecef',
                                borderRadius: '10px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${percentage}%`,
                                  height: '100%',
                                  backgroundColor: percentage > 80 ? '#dc3545' : percentage > 50 ? '#ffc107' : '#28a745',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Rezervasyon Butonu */}
                      {getBookedBreakfastCount(selectedBreakfastDate) < pricing.breakfast.capacity && (
                        <button
                          onClick={() => handleManualBreakfastReservation(selectedBreakfastDate)}
                          style={{
                            width: '100%',
                            padding: '1rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                        >
                          ➕ Yeni Kahvaltı Rezervasyonu Yap
                        </button>
                      )}
                      
                      {getBookedBreakfastCount(selectedBreakfastDate) >= pricing.breakfast.capacity && (
                        <div style={{
                          padding: '1rem',
                          backgroundColor: '#f8d7da',
                          border: '1px solid #f5c6cb',
                          borderRadius: '8px',
                          color: '#721c24',
                          textAlign: 'center',
                          fontWeight: '600'
                        }}>
                          ⚠️ Kapasite Dolu - Yeni rezervasyon yapılamaz
                        </div>
                      )}
                    </>
                  )}

                  {!selectedBreakfastDate && (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      color: '#6c757d'
                    }}>
                      👆 Lütfen yukarıdan bir tarih seçin
                    </div>
                  )}
                </div>
              </div>
              
              <div className="menu-section" style={{ marginTop: '30px' }}>
                <h3>Menü Düzenleyici</h3>
                <p className="info-text">
                  ℹ️ <strong>Kullanım:</strong> Altını çizme, kalın yazı, madde işaretleri gibi tüm formatlama araçlarını kullanabilirsiniz.
                </p>
                
                <div className="rich-editor-container">
                  <RichTextEditor
                    value={breakfastMenuHTML}
                    onChange={handleBreakfastMenuChange}
                  />
                </div>

                <div className="menu-preview" style={{ marginTop: '20px' }}>
                  <h4>📋 Önizleme</h4>
                  <div 
                    className="menu-preview-content"
                    dangerouslySetInnerHTML={{ __html: breakfastMenuHTML }}
                  />
                </div>
              </div>

              <div className="pricing-section" style={{ marginTop: '30px' }}>
                <h3>Fiyatlandırma</h3>
                <div className="pricing-grid">
                  <div className="pricing-item">
                    <label>Yetişkin Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.breakfast.adultPrice}
                      onChange={(e) => handlePricingUpdate('breakfast', 'adultPrice', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>4-6 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.breakfast.child4_6Price}
                      onChange={(e) => handlePricingUpdate('breakfast', 'child4_6Price', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>7-12 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.breakfast.child7_12Price}
                      onChange={(e) => handlePricingUpdate('breakfast', 'child7_12Price', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>0-3 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={0}
                      disabled
                      className="input-field"
                    />
                    <small>Ücretsiz</small>
                  </div>
                </div>
              </div>

              <div className="save-section" style={{ marginTop: '20px' }}>
                <button onClick={handleSaveAll} className="btn-save-large">
                  💾 Değişiklikleri Kaydet
                </button>
              </div>
            </div>
          )}

          {/* Akşam Yemeği */}
          {activeTab === 'dinner' && (
            <div className="tab-content">
              <h2>🍽️ Akşam Yemeği Yönetimi</h2>
              
              <div className="pricing-section">
                <h3>Salon Kapasitesi</h3>
                <div className="pricing-item" style={{ maxWidth: '300px' }}>
                  <label>Maksimum Kapasite (Kişi)</label>
                  <input
                    type="number"
                    value={pricing.dinner.capacity}
                    onChange={(e) => handlePricingUpdate('dinner', 'capacity', parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="0"
                  />
                </div>
              </div>

              {/* Kapalı Günler */}
              <div className="pricing-section" style={{ marginTop: '24px' }}>
                <h3>🌐 Web Satış Durumu</h3>
                <div className="pricing-item" style={{ maxWidth: '380px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <input
                      type="checkbox"
                      checked={dinnerWebEnabled}
                      onChange={(e) => {
                        const nextEnabled = e.target.checked;
                        setDinnerWebEnabled(nextEnabled);
                        saveDinnerPublicSettings(nextEnabled, dinnerClosedDays);
                      }}
                    />
                    Akşam yemeği web satışı aktif
                  </label>
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
                    Kapalıyken anasayfa/menüden akşam yemeği rezervasyonu alınmaz. Konaklama sırasında akşam yemeği ekleme açık kalır.
                  </small>
                </div>
              </div>

              <div className="pricing-section" style={{ marginTop: '24px' }}>
                <h3>🚫 Kapalı Günler</h3>
                <p className="info-text">Seçilen günlerde rezervasyon sayfasında tarih seçilemez ve uyarı gösterilir.</p>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  {[['Pazar', 0], ['Pazartesi', 1], ['Salı', 2], ['Çarşamba', 3], ['Perşembe', 4], ['Cuma', 5], ['Cumartesi', 6]].map(([name, val]) => (
                    <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', background: dinnerClosedDays.includes(val as number) ? '#ffebee' : '#f8f9fa', border: `1px solid ${dinnerClosedDays.includes(val as number) ? '#e53935' : '#dee2e6'}`, borderRadius: '20px', cursor: 'pointer', userSelect: 'none', fontWeight: dinnerClosedDays.includes(val as number) ? '600' : '400', color: dinnerClosedDays.includes(val as number) ? '#c62828' : '#495057', transition: 'all 0.2s' }}>
                      <input
                        type="checkbox"
                        checked={dinnerClosedDays.includes(val as number)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...dinnerClosedDays, val as number].sort()
                            : dinnerClosedDays.filter(d => d !== val);
                          setDinnerClosedDays(next);
                          saveDinnerPublicSettings(dinnerWebEnabled, next);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      {name}
                    </label>
                  ))}
                </div>
                <p style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: '#999' }}>Varsayılan: Pazartesi kapalı. Değişiklikler "Değişiklikleri Kaydet" ile sunucuya yazılır.</p>
              </div>
              
              {/* Rezervasyon Takvimi */}
              <div className="menu-section" style={{ marginTop: '30px' }}>
                <h3>📅 Rezervasyon Takvimi</h3>
                <p className="info-text">
                  ℹ️ Her gün için rezerve edilen akşam yemeği sayısını görün. Detaylar için tarihe tıklayın.
                </p>
                
                <div style={{ marginTop: '1rem' }}>
                  {/* Takvim Navigasyon */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <button onClick={() => setDinnerMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() - 1); return d; })} className="btn-add" style={{ padding: '0.5rem 1rem' }}>
                      ◀ Önceki
                    </button>
                    <h4 style={{ margin: 0, fontSize: '1rem' }}>
                      {dinnerMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                      {' - '}
                      {(() => {
                        const nextMonth = new Date(dinnerMonth);
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        return nextMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
                      })()}
                    </h4>
                    <button onClick={() => setDinnerMonth(prev => { const d = new Date(prev); d.setMonth(d.getMonth() + 1); return d; })} className="btn-add" style={{ padding: '0.5rem 1rem' }}>
                      Sonraki ▶
                    </button>
                  </div>

                  {/* İki Takvim Yan Yana */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {[dinnerMonth, (() => { const next = new Date(dinnerMonth); next.setMonth(next.getMonth() + 1); return next; })()].map((monthDate, monthIndex) => {
                      const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(monthDate);
                      
                      return (
                        <div key={monthIndex}>
                          <h5 style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.95rem', color: '#495057' }}>
                            {monthDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                          </h5>
                          
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(7, 1fr)', 
                            gap: '0.3rem'
                          }}>
                            {/* Gün Başlıkları */}
                            {['P', 'S', 'Ç', 'P', 'C', 'C', 'P'].map((day, idx) => (
                              <div key={idx} style={{ 
                                textAlign: 'center', 
                                fontWeight: 'bold', 
                                fontSize: '0.7rem',
                                padding: '0.3rem',
                                color: '#6c757d',
                                backgroundColor: idx >= 5 ? '#e8f5e9' : 'transparent'
                              }}>
                                {day}
                              </div>
                            ))}

                            {/* Boş hücreler */}
                            {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                              <div key={`empty-${i}`} />
                            ))}

                            {/* Günler */}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                              const day = i + 1;
                              const dateStr = formatDateForBooking(year, month, day);
                              const bookedCount = getBookedDinnerCount(dateStr);
                              const totalCount = pricing.dinner.capacity;
                              const isSelected = selectedDinnerDate === dateStr;
                              const isToday = new Date().toISOString().split('T')[0] === dateStr;
                              const dayOfWeek = new Date(year, month, day).getDay();
                              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                              
                              return (
                                <div
                                  key={day}
                                  onClick={() => setSelectedDinnerDate(dateStr)}
                                  style={{
                                    border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    padding: '0.3rem',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isToday ? '#e7f3ff' : (bookedCount > 0 ? '#fff3cd' : (isWeekend ? '#e8f5e9' : '#f8f9fa')),
                                    transition: 'all 0.2s',
                                    minHeight: '45px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    fontSize: '0.8rem',
                                    position: 'relative'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)'}
                                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                                >
                                  <div style={{ fontWeight: '600', width: '100%', textAlign: 'center' }}>
                                    {day}
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.65rem', 
                                    color: bookedCount > 0 ? '#856404' : '#6c757d', 
                                    fontWeight: '600',
                                    alignSelf: 'flex-end'
                                  }}>
                                    {bookedCount}/{totalCount}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Seçili Tarih Detayları */}
                  {selectedDinnerDate && (
                    <div style={{ 
                      marginTop: '1.5rem', 
                      padding: '1rem', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #dee2e6'
                    }}>
                      <h4 style={{ marginBottom: '1rem' }}>
                        {new Date(selectedDinnerDate + 'T00:00:00').toLocaleDateString('tr-TR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })} - Akşam Yemeği Rezervasyonları
                      </h4>
                      
                      {(() => {
                        const dayBookings = getBookingsForDate(selectedDinnerDate, 'dinner');
                        
                        if (dayBookings.length === 0) {
                          return (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                              Bu tarih için rezervasyon bulunmamaktadır.
                            </p>
                          );
                        }
                        
                        return (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ 
                              width: '100%', 
                              borderCollapse: 'collapse',
                              backgroundColor: '#fff'
                            }}>
                              <thead>
                                <tr style={{ backgroundColor: '#e9ecef' }}>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                                    Ad Soyad
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                                    Telefon
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Yetişkin
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    4-6 Yaş
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    7-12 Yaş
                                  </th>
                                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                                    Toplam
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayBookings.map((booking, index) => (
                                  <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '0.75rem' }}>
                                      {booking.customerName || 'Belirtilmemiş'}
                                    </td>
                                    <td style={{ padding: '0.75rem' }}>
                                      {booking.customerPhone || 'Belirtilmemiş'}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                      {booking.adults || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                      {booking.children4_6 || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                      {booking.children7_12 || 0}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600' }}>
                                      {(booking.adults || 0) + (booking.children4_6 || 0) + (booking.children7_12 || 0)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div style={{ marginTop: '1rem', textAlign: 'right', fontWeight: '600', fontSize: '1rem' }}>
                              Toplam: {getBookedDinnerCount(selectedDinnerDate)} kişi
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Manuel Rezervasyon */}
              <div className="menu-section" style={{ marginTop: '30px' }}>
                <h3>🎯 Manuel Rezervasyon</h3>
                <p className="info-text">
                  ℹ️ Kapıdan gelen müşteriler için buradan akşam yemeği rezervasyonu yapabilirsiniz.
                </p>
                
                <div style={{ marginTop: '1.5rem' }}>
                  {/* Tarih Seçimi */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                      📅 Tarih Seçin:
                    </label>
                    <input
                      type="date"
                      value={selectedDinnerDate || ''}
                      onChange={(e) => setSelectedDinnerDate(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {selectedDinnerDate && (
                    <>
                      {/* Kapasite Bilgisi */}
                      {(() => {
                        const bookedCount = getBookedDinnerCount(selectedDinnerDate);
                        const totalCapacity = pricing.dinner.capacity;
                        const available = totalCapacity - bookedCount;
                        const percentage = (bookedCount / totalCapacity) * 100;
                        
                        return (
                          <div style={{
                            border: '2px solid #dee2e6',
                            borderRadius: '8px',
                            padding: '1.5rem',
                            backgroundColor: '#fff',
                            marginBottom: '1rem'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                              <div>
                                <h4 style={{ margin: 0, fontSize: '1.1rem' }}>Kapasite Durumu</h4>
                                <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                  {new Date(selectedDinnerDate + 'T00:00:00').toLocaleDateString('tr-TR', { 
                                    day: 'numeric', 
                                    month: 'long', 
                                    year: 'numeric' 
                                  })}
                                </p>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: available > 0 ? '#28a745' : '#dc3545' }}>
                                  {available}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>kişi müsait</div>
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: '0.5rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '0.3rem' }}>
                                <span>Dolu: {bookedCount} kişi</span>
                                <span>Toplam: {totalCapacity} kişi</span>
                              </div>
                              <div style={{ 
                                width: '100%', 
                                height: '20px', 
                                backgroundColor: '#e9ecef',
                                borderRadius: '10px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${percentage}%`,
                                  height: '100%',
                                  backgroundColor: percentage > 80 ? '#dc3545' : percentage > 50 ? '#ffc107' : '#28a745',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Rezervasyon Butonu */}
                      {getBookedDinnerCount(selectedDinnerDate) < pricing.dinner.capacity && (
                        <button
                          onClick={() => handleManualDinnerReservation(selectedDinnerDate)}
                          style={{
                            width: '100%',
                            padding: '1rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                        >
                          ➕ Yeni Akşam Yemeği Rezervasyonu Yap
                        </button>
                      )}
                      
                      {getBookedDinnerCount(selectedDinnerDate) >= pricing.dinner.capacity && (
                        <div style={{
                          padding: '1rem',
                          backgroundColor: '#f8d7da',
                          border: '1px solid #f5c6cb',
                          borderRadius: '8px',
                          color: '#721c24',
                          textAlign: 'center',
                          fontWeight: '600'
                        }}>
                          ⚠️ Kapasite Dolu - Yeni rezervasyon yapılamaz
                        </div>
                      )}
                    </>
                  )}

                  {!selectedDinnerDate && (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      color: '#6c757d'
                    }}>
                      👆 Lütfen yukarıdan bir tarih seçin
                    </div>
                  )}
                </div>
              </div>
              
              <div className="menu-section" style={{ marginTop: '30px' }}>
                <h3>Menü Düzenleyici</h3>
                <p className="info-text">
                  ℹ️ <strong>Kullanım:</strong> Altını çizme, kalın yazı, madde işaretleri gibi tüm formatlama araçlarını kullanabilirsiniz.
                </p>
                
                <div className="rich-editor-container">
                  <RichTextEditor
                    value={dinnerMenuHTML}
                    onChange={handleDinnerMenuChange}
                  />
                </div>

                <div className="menu-preview" style={{ marginTop: '20px' }}>
                  <h4>📋 Önizleme</h4>
                  <div 
                    className="menu-preview-content"
                    dangerouslySetInnerHTML={{ __html: dinnerMenuHTML }}
                  />
                </div>
              </div>

              <div className="pricing-section" style={{ marginTop: '30px' }}>
                <h3>Fiyatlandırma</h3>
                <div className="pricing-grid">
                  <div className="pricing-item">
                    <label>Yetişkin Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.dinner.adultPrice}
                      onChange={(e) => handlePricingUpdate('dinner', 'adultPrice', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>0-3 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={0}
                      disabled
                      className="input-field"
                    />
                    <small>Ücretsiz</small>
                  </div>
                  <div className="pricing-item">
                    <label>4-6 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.dinner.child4_6Price}
                      onChange={(e) => handlePricingUpdate('dinner', 'child4_6Price', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>
                  <div className="pricing-item">
                    <label>7-12 Yaş Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={pricing.dinner.child7_12Price}
                      onChange={(e) => handlePricingUpdate('dinner', 'child7_12Price', parseFloat(e.target.value))}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              <div className="save-section" style={{ marginTop: '20px' }}>
                <button onClick={handleSaveAll} className="btn-save-large">
                  💾 Değişiklikleri Kaydet
                </button>
              </div>
            </div>
          )}

          {/* Kazançlar */}
          {activeTab === 'revenue' && (
            <div className="tab-content">
              <h2>💰 Kazançlar</h2>
              
              {/* Özet Kartlar */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
                {/* Günlük Kazanç */}
                {(() => {
                  const dailyRevenue = getDailyRevenue();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', opacity: 0.9 }}>📅 Günlük</h3>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        ₺{dailyRevenue.total.toLocaleString('tr-TR')}
                      </div>
                    </div>
                  );
                })()}

                {/* Haftalık Kazanç */}
                {(() => {
                  const weeklyRevenue = getWeeklyRevenue();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', opacity: 0.9 }}>📆 Haftalık</h3>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        ₺{weeklyRevenue.total.toLocaleString('tr-TR')}
                      </div>
                    </div>
                  );
                })()}

                {/* Aylık Kazanç */}
                {(() => {
                  const monthlyRevenue = getMonthlyRevenue();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', opacity: 0.9 }}>📊 Aylık</h3>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        ₺{monthlyRevenue.total.toLocaleString('tr-TR')}
                      </div>
                    </div>
                  );
                })()}

                {/* Toplam Kazanç */}
                {(() => {
                  const totalRevenue = getTotalRevenue();
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', opacity: 0.9 }}>💎 Toplam</h3>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        ₺{totalRevenue.total.toLocaleString('tr-TR')}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Detaylı Tablo */}
              <div style={{ 
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>📈 Detaylı Gelir Raporu</h3>
                
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Dönem</th>
                      <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600' }}>� Oda</th>
                      <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600' }}>🏖️ Şezlong</th>
                      <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600' }}>🍳 Kahvaltı</th>
                      <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600' }}>🍽️ Akşam Yemeği</th>
                      <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: '#667eea' }}>💰 Toplam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Günlük */}
                    {(() => {
                      const revenue = getDailyRevenue();
                      return (
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '1rem', fontWeight: '600' }}>Bugün</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.room.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.sunbed.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.breakfast.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.dinner.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: '#667eea' }}>
                            ₺{revenue.total.toLocaleString('tr-TR')}
                          </td>
                        </tr>
                      );
                    })()}

                    {/* Haftalık */}
                    {(() => {
                      const revenue = getWeeklyRevenue();
                      return (
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '1rem', fontWeight: '600' }}>Bu Hafta</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.room.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.sunbed.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.breakfast.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.dinner.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: '#667eea' }}>
                            ₺{revenue.total.toLocaleString('tr-TR')}
                          </td>
                        </tr>
                      );
                    })()}

                    {/* Aylık */}
                    {(() => {
                      const revenue = getMonthlyRevenue();
                      return (
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '1rem', fontWeight: '600' }}>Bu Ay</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.room.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.sunbed.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.breakfast.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.dinner.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: '#667eea' }}>
                            ₺{revenue.total.toLocaleString('tr-TR')}
                          </td>
                        </tr>
                      );
                    })()}

                    {/* Toplam */}
                    {(() => {
                      const revenue = getTotalRevenue();
                      return (
                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: '700' }}>
                          <td style={{ padding: '1rem', fontWeight: '700' }}>TOPLAM</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.room.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.sunbed.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.breakfast.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right' }}>₺{revenue.dinner.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: '#667eea', fontSize: '1.1rem' }}>
                            ₺{revenue.total.toLocaleString('tr-TR')}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Bilgilendirme */}
              <div style={{ 
                marginTop: '1.5rem',
                padding: '1.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #dee2e6'
              }}>
                <h3 style={{ marginTop: 0 }}>ℹ️ Bilgilendirme</h3>
                <ul style={{ marginBottom: 0, lineHeight: '1.8' }}>
                  <li><strong>Günlük:</strong> Bugünün tamamlanmış rezervasyonlarından elde edilen gelir</li>
                  <li><strong>Haftalık:</strong> Bu haftanın (Pazartesi-Pazar) toplam geliri</li>
                  <li><strong>Aylık:</strong> Bu ayın toplam geliri</li>
                  <li><strong>Toplam:</strong> Tüm zamanların toplam geliri</li>
                  <li><strong>Not:</strong> Sadece tamamlanmış (ödeme alınmış) rezervasyonlar hesaba katılır</li>
                </ul>
              </div>
            </div>
          )}

          {/* Otel Entegrasyonu */}
          {activeTab === 'hotel-integration' && (
            <HotelIntegrationTab />
          )}

          {/* Odalar Yönetimi */}
          {activeTab === 'rooms' && (
            <RoomsManagement />
          )}

          {/* TGA Entegrasyon */}
          {activeTab === 'tga-integration' && (
            <TgaIntegrationTab />
          )}

          {/* Bildirim Ayarları */}
          {activeTab === 'notifications' && (
            <NotificationSettingsTab />
          )}

          {/* Genel Ayarlar */}
          {activeTab === 'general-settings' && (
            <GeneralSettingsTab />
          )}

          {/* Ana Sayfa İçerikleri */}
          {activeTab === 'homepage-content' && (
            <HomepageContentTab />
          )}

          {/* Kapalı Tarihler */}
          {activeTab === 'blocked-dates' && (
            <BlockedDatesTab />
          )}

          {/* Rezervasyonlar */}
          {activeTab === 'bookings' && (
            <div className="tab-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2>📋 Tüm Rezervasyonlar</h2>
                <button 
                  onClick={() => setShowManualBookingModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#229954'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#27ae60'}
                >
                  ➕ Manuel Oda Rezervasyonu
                </button>
                <button
                  onClick={() => setShowManualSunbedModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#e67e22',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    marginLeft: '0.5rem'
                  }}
                >
                  🏖️ Manuel Şezlong Rezervasyonu
                </button>
              </div>
              
              {/* Filtreler */}
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '1.5rem', 
                flexWrap: 'wrap',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div style={{ flex: '1', minWidth: '150px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.85rem' }}>Tür</label>
                  <select 
                    value={bookingsFilter.product_type}
                    onChange={(e) => setBookingsFilter(f => ({ ...f, product_type: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Tümü</option>
                    <option value="room">Oda</option>
                    <option value="sunbed">Şezlong</option>
                    <option value="breakfast">Kahvaltı</option>
                    <option value="dinner">Akşam Yemeği</option>
                  </select>
                </div>
                <div style={{ flex: '1', minWidth: '150px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.85rem' }}>Durum</label>
                  <select 
                    value={bookingsFilter.status}
                    onChange={(e) => setBookingsFilter(f => ({ ...f, status: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Tümü</option>
                    <option value="pending_payment">Ödeme Bekliyor</option>
                    <option value="paid">Ödendi</option>
                    <option value="confirmed">Onaylandı</option>
                    <option value="paid_pending_confirmation">Ödendi (Onay Bekliyor)</option>
                    <option value="cancelled">İptal</option>
                    <option value="completed">Tamamlandı</option>
                  </select>
                </div>
                <div style={{ flex: '1', minWidth: '150px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.85rem' }}>Başlangıç</label>
                  <input 
                    type="date"
                    value={bookingsFilter.start_date}
                    onChange={(e) => setBookingsFilter(f => ({ ...f, start_date: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ flex: '1', minWidth: '150px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.85rem' }}>Bitiş</label>
                  <input 
                    type="date"
                    value={bookingsFilter.end_date}
                    onChange={(e) => setBookingsFilter(f => ({ ...f, end_date: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button 
                    onClick={() => setBookingsFilter({ product_type: '', status: '', start_date: '', end_date: '' })}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Filtreleri Temizle
                  </button>
                </div>
              </div>

              {bookingsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>⏳ Yükleniyor...</p>
                </div>
              ) : bookings.length === 0 ? (
                <div className="empty-state">
                  <p>Henüz rezervasyon bulunmuyor.</p>
                  <small>Müşteriler rezervasyon yaptığında burada görünecektir.</small>
                </div>
              ) : (
                <>
                  {/* Landscape Mode Uyarısı (Mobil portrait modda) */}
                  <div className="landscape-hint">
                    📱 💡 <strong>İpucu:</strong> Daha iyi görüntüleme için telefonunuzu yatay konuma çevirebilirsiniz.
                  </div>

                  {/* Masaüstü Tablo Görünümü */}
                  <div className="bookings-table-wrapper" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Rezervasyon No</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Tür</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Tarih</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Müşteri</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Telefon</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Detaylar</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tutar</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Durum</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Ödeme</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const productTypeLabels: { [key: string]: string } = {
                          room: '🏨 Oda',
                          sunbed: '🏖️ Şezlong',
                          breakfast: '🍳 Kahvaltı',
                          dinner: '🍽️ Akşam Yemeği'
                        };
                        const statusLabels: { [key: string]: { text: string; color: string } } = {
                          pending_payment: { text: 'Ödeme Bekliyor', color: '#f39c12' },
                          paid: { text: 'Ödendi', color: '#27ae60' },
                          confirmed: { text: 'Onaylandı', color: '#27ae60' },
                          paid_pending_confirmation: { text: 'Onay Bekliyor', color: '#3498db' },
                          cancelled: { text: 'İptal', color: '#e74c3c' },
                          completed: { text: 'Tamamlandı', color: '#95a5a6' },
                          pending: { text: 'Beklemede', color: '#f39c12' }
                        };
                        const paymentLabels: { [key: string]: { text: string; color: string } } = {
                          pending: { text: 'Bekliyor', color: '#f39c12' },
                          paid: { text: 'Ödendi', color: '#27ae60' },
                          failed: { text: 'Başarısız', color: '#e74c3c' }
                        };

                        // Oda rezervasyonuna bağlı otomatik oluşturulan alt kayıtları grupla
                        const childMap: { [parentId: string]: any[] } = {};
                        const topLevel: any[] = [];
                        bookings.forEach(b => {
                          const parentId = b.customer_info?.parentBookingId;
                          if (parentId && b.customer_info?.autoCreated) {
                            if (!childMap[parentId]) childMap[parentId] = [];
                            childMap[parentId].push(b);
                          } else {
                            topLevel.push(b);
                          }
                        });

                        const renderBookingActions = (booking: any) => (
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {booking.status !== 'cancelled' && (
                              <button
                                onClick={async () => {
                                  const reason = window.prompt('İptal sebebini yazın (opsiyonel):');
                                  if (window.confirm(`Bu rezervasyonu iptal etmek istediğinize emin misiniz?\n\n${booking.reservation_id}`)) {
                                    try {
                                      await adminAPI.cancelBooking(booking.id.toString(), reason || undefined);
                                      alert('Rezervasyon iptal edildi');
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('İptal hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="Rezervasyonu İptal Et"
                              >❌ İptal</button>
                            )}
                            {booking.status === 'cancelled' && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Bu rezervasyonu kalıcı olarak silmek istediğinize emin misiniz?\n\n${booking.reservation_id}\n\n⚠️ Bu işlem geri alınamaz!`)) {
                                    try {
                                      await adminAPI.deleteBooking(booking.id.toString());
                                      alert('Rezervasyon silindi');
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('Silme hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="Rezervasyonu Kalıcı Sil"
                              >🗑️ Sil</button>
                            )}
                            {booking.status === 'paid_pending_confirmation' && (
                              <button
                                onClick={async () => {
                                  try {
                                    if (booking.product_type === 'room') {
                                      if (window.confirm(`Bu rezervasyon ödemesi alınmış ancak AsistPro onayı eksik görünüyor.\n\n${booking.reservation_id}\n\nAsistPro eşleme/onay işlemi şimdi yeniden denenecek. Devam edilsin mi?`)) {
                                        const result = await adminAPI.manualConfirmBooking(booking.id.toString());
                                        alert('✅ ' + (result.data?.message || 'Rezervasyon toparlandı'));
                                      }
                                    } else {
                                      await adminAPI.updateBookingStatus(booking.id.toString(), 'confirmed');
                                      alert('Rezervasyon onaylandı');
                                    }
                                    fetchBookings();
                                  } catch (err: any) {
                                    alert('Onay hatası: ' + (err.response?.data?.message || err.message));
                                  }
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title={booking.product_type === 'room' ? 'AsistPro eşleme ve onayı yeniden dene' : 'Rezervasyonu Onayla'}
                              >{booking.product_type === 'room' ? '🔄 Toparla' : '✅ Onayla'}</button>
                            )}
                            {(booking.status === 'pending_payment' || booking.status === 'pending') && booking.product_type === 'room' && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Bu rezervasyonu manuel olarak onaylamak istediğinize emin misiniz?\n\n${booking.reservation_id}\n\nÖdeme alındığından emin olun!`)) {
                                    try {
                                      const result = await adminAPI.manualConfirmBooking(booking.id.toString());
                                      alert('✅ ' + (result.data?.message || 'Rezervasyon onaylandı'));
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('Onay hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#2980b9', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="Manuel Onayla (Ödeme alındı ise)"
                              >🔧 Manuel Onayla</button>
                            )}
                            {booking.status !== 'cancelled' && booking.invoice_type && booking.invoice_status !== 'created' && booking.invoice_status !== 'draft' && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Bu rezervasyon için Paraşüt faturası oluşturulacak.\n\n${booking.reservation_id}\nTip: ${booking.invoice_type === 'individual' ? 'Bireysel' : 'Kurumsal'}\nAd: ${booking.invoice_name || '-'}\n\nDevam edilsin mi?`)) {
                                    try {
                                      const result = await adminAPI.createInvoice(booking.id.toString());
                                      const msg = result.data?.data?.officializeSuccess === false
                                        ? '⚠️ Fatura oluşturuldu ancak taslakta kaldı. Paraşüt üzerinden e-Fatura gönderebilirsiniz.'
                                        : '✅ ' + (result.data?.message || 'Fatura oluşturuldu');
                                      alert(msg);
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('Fatura hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="Paraşüt Faturası Oluştur"
                              >🧾 Fatura Oluştur</button>
                            )}
                            {booking.invoice_status === 'draft' && booking.parasut_invoice_id && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Taslak fatura yeniden resmileştirilecek (e-Fatura/e-Arşiv gönderimi).\n\nParaşüt ID: ${booking.parasut_invoice_id}\nRezervasyon: ${booking.reservation_id}\n\nDevam edilsin mi?`)) {
                                    try {
                                      const result = await adminAPI.retryOfficialize(booking.id.toString());
                                      alert('✅ ' + (result.data?.message || 'Fatura resmileştirildi'));
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('Resmileştirme hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                title="Taslak Faturayı Yeniden Resmileştir"
                              >📤 Yeniden Gönder</button>
                            )}
                          </div>
                        );

                        const renderDetailCell = (booking: any) => (
                          <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                            {booking.product_type === 'sunbed' && (booking.customer_info?.zoneDetails || booking.items?.zones) && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                                {(booking.customer_info?.zoneDetails || booking.items?.zones || []).map((z: any, i: number) => (
                                  <span key={i} style={{ backgroundColor: '#e8f4fd', padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                    {z.label ? z.label : `B${z.zone_id}`} · {z.person_count || 2}k
                                  </span>
                                ))}
                                <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.25rem' }}>
                                  ({booking.items?.totalPersons || (booking.customer_info?.zoneDetails || booking.items?.zones || []).reduce((s: number, z: any) => s + (z.person_count || 2), 0)} kişi)
                                </span>
                              </div>
                            )}
                            {booking.product_type === 'sunbed' && !booking.customer_info?.zoneDetails && !booking.items?.zones && booking.items?.sunbeds && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {booking.items.sunbeds.map((s: any, i: number) => (
                                  <span key={i} style={{ backgroundColor: '#e8f4fd', padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.75rem' }}>S{s.row}-{s.number}</span>
                                ))}
                              </div>
                            )}
                            {booking.product_type === 'breakfast' && (
                              <span>{(() => {
                                const items = booking.items || {};
                                const adults = (items.adults || 0) + (items.men || 0) + (items.women || 0);
                                const children = items.children?.length || (items.children0_6 || 0) + (items.children7_12 || 0);
                                const total = items.totalPersons || adults + children || 1;
                                return adults > 0 && children > 0 ? `${adults} yetişkin + ${children} çocuk` : `${total} kişi`;
                              })()}</span>
                            )}
                            {booking.product_type === 'dinner' && (
                              <span>{(() => {
                                const items = booking.items || {};
                                const adults = (items.adults || 0) + (items.men || 0) + (items.women || 0);
                                const children = items.children?.length || 0;
                                const total = items.totalPersons || adults + children || 1;
                                return adults > 0 && children > 0 ? `${adults} yetişkin + ${children} çocuk` : `${total} kişi`;
                              })()}</span>
                            )}
                            {booking.product_type === 'room' && (
                              <span>
                                {booking.room_number || booking.items?.roomTypeName || booking.room_type_label || ''}
                                {booking.customer_info?.adults ? ` ${booking.customer_info.adults}Y` : ''}
                                {booking.customer_info?.children ? `+${booking.customer_info.children}Ç` : ''}
                                {booking.customer_info?.nights ? ` · ${booking.customer_info.nights}G` : ''}
                              </span>
                            )}
                            {!['sunbed', 'breakfast', 'dinner', 'room'].includes(booking.product_type) && '-'}
                          </td>
                        );

                        const rows: React.ReactNode[] = [];

                        topLevel.forEach((booking, index) => {
                          const children = childMap[booking.id] || [];
                          const hasChildren = children.length > 0;
                          const isExpanded = expandedRooms.has(booking.id);

                          // Ana satır
                          rows.push(
                            <tr key={booking.id || index} style={{ borderBottom: hasChildren && isExpanded ? 'none' : '1px solid #eee', backgroundColor: hasChildren ? '#f8f9fa' : 'white' }}>
                              <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {hasChildren && (
                                    <button
                                      onClick={() => setExpandedRooms(prev => {
                                        const next = new Set(prev);
                                        if (next.has(booking.id)) next.delete(booking.id); else next.add(booking.id);
                                        return next;
                                      })}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '0', lineHeight: 1, color: '#2c3e50', fontWeight: 'bold' }}
                                      title={isExpanded ? 'Alt ürünleri gizle' : `${children.length} alt ürün göster`}
                                    >
                                      {isExpanded ? '▼' : '▶'}
                                    </button>
                                  )}
                                  <span>{booking.reservation_id || '-'}</span>
                                  {booking.asistpro_confirmation_code && booking.asistpro_confirmation_code !== booking.reservation_id && (
                                    <span style={{ display: 'block', fontSize: '0.7rem', color: '#27ae60', fontWeight: 'bold', marginTop: '2px' }} title="AsistPro Rezervasyon No">
                                      📋 {booking.asistpro_confirmation_code}
                                    </span>
                                  )}
                                  {hasChildren && (
                                    <span style={{ fontSize: '0.7rem', backgroundColor: '#3498db', color: 'white', borderRadius: '10px', padding: '0.1rem 0.4rem' }}>
                                      +{children.length}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                {productTypeLabels[booking.product_type] || booking.product_type}
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                {booking.created_at && (
                                  <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '2px' }}>
                                    🕐 {new Date(booking.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                  </div>
                                )}
                                {booking.product_type === 'room' && booking.customer_info?.checkIn ? (
                                  <div style={{ fontSize: '0.85rem' }}>
                                    {new Date(booking.customer_info.checkIn.split('T')[0] + 'T12:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                                    {' → '}
                                    {booking.customer_info.checkOut ? new Date(booking.customer_info.checkOut.split('T')[0] + 'T12:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) : ''}
                                  </div>
                                ) : booking.booking_date ? (
                                  <div style={{ fontSize: '0.85rem' }}>
                                    {new Date(booking.booking_date).toLocaleDateString('tr-TR')}
                                  </div>
                                ) : null}
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                <div>{booking.customer_name || '-'}</div>
                                <small style={{ color: '#666' }}>{booking.customer_email || ''}</small>
                              </td>
                              <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>{booking.customer_phone || '-'}</td>
                              {renderDetailCell(booking)}
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                                ₺{(booking.total_price || 0).toLocaleString('tr-TR')}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: statusLabels[booking.status]?.color || '#666', color: 'white' }}>
                                  {statusLabels[booking.status]?.text || booking.status}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                <span style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: paymentLabels[booking.payment_status]?.color || '#666', color: 'white' }}>
                                  {paymentLabels[booking.payment_status]?.text || booking.payment_status || '-'}
                                </span>
                                {booking.invoice_type && (
                                  <span style={{ display: 'block', marginTop: '4px', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', color: 'white',
                                    backgroundColor: booking.invoice_status === 'created' ? '#27ae60' : booking.invoice_status === 'draft' ? '#e67e22' : booking.invoice_status === 'failed' ? '#e74c3c' : '#95a5a6' }}
                                    title={booking.invoice_status === 'created' ? `Paraşüt ID: ${booking.parasut_invoice_id}` : booking.invoice_status === 'draft' ? 'Fatura oluştu ama resmileştirilemedi (taslak)' : 'Fatura kesilemedi'}>
                                    {booking.invoice_status === 'created' ? (
                                      booking.invoice_pdf_url
                                        ? <a href={booking.invoice_pdf_url} target="_blank" rel="noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>🧾 Fatura PDF</a>
                                        : '🧾 Fatura Kesildi'
                                    ) : booking.invoice_status === 'draft' ? '⚠️ Taslak Fatura' : booking.invoice_status === 'failed' ? '🧾 Fatura Hatası' : '🧾 Fatura Bekliyor'}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                {renderBookingActions(booking)}
                              </td>
                            </tr>
                          );

                          // Alt satırlar (sadece expand edilmişse)
                          if (hasChildren && isExpanded) {
                            children
                              .sort((a: any, b: any) => new Date(a.booking_date).getTime() - new Date(b.booking_date).getTime())
                              .forEach((child: any, ci: number) => {
                                const isLast = ci === children.length - 1;
                                rows.push(
                                  <tr key={`child-${child.id}`} style={{ borderBottom: isLast ? '2px solid #dee2e6' : '1px solid #f0f0f0', backgroundColor: '#fefefe' }}>
                                    <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.8rem', paddingLeft: '2.5rem', color: '#555' }}>
                                      <span style={{ color: '#aaa', marginRight: '0.3rem' }}>└</span>
                                      {child.reservation_id || '-'}
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}>
                                      <span style={{ color: '#7f8c8d' }}>{productTypeLabels[child.product_type] || child.product_type}</span>
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#555' }}>
                                      {child.created_at && (
                                        <div style={{ fontSize: '0.7rem', color: '#aaa' }}>
                                          🕐 {new Date(child.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                        </div>
                                      )}
                                      {child.booking_date ? new Date(child.booking_date).toLocaleDateString('tr-TR') : '-'}
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem' }}>
                                      <small style={{ color: '#888' }}>↳ oda ile</small>
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem' }}>-</td>
                                    {renderDetailCell(child)}
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: '500', fontSize: '0.9rem', color: '#555' }}>
                                      ₺{(child.total_price || 0).toLocaleString('tr-TR')}
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                      <span style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', backgroundColor: statusLabels[child.status]?.color || '#666', color: 'white' }}>
                                        {statusLabels[child.status]?.text || child.status}
                                      </span>
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                      <span style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', backgroundColor: paymentLabels[child.payment_status]?.color || '#666', color: 'white' }}>
                                        {paymentLabels[child.payment_status]?.text || child.payment_status || '-'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                      {renderBookingActions(child)}
                                    </td>
                                  </tr>
                                );
                              });
                          }
                        });

                        return rows;
                      })()}
                    </tbody>
                  </table>
                  <div style={{ marginTop: '1rem', textAlign: 'right', color: '#666' }}>
                    {(() => {
                      const childCount = bookings.filter(b => b.customer_info?.parentBookingId && b.customer_info?.autoCreated).length;
                      const topCount = bookings.length - childCount;
                      return childCount > 0
                        ? `Toplam: ${topCount} rezervasyon (${childCount} alt ürün dahil değil)`
                        : `Toplam: ${topCount} rezervasyon`;
                    })()}
                  </div>
                </div>

                {/* Mobil Card Görünümü */}
                <div className="bookings-mobile-cards">
                  {(() => {
                    const productTypeLabels: { [key: string]: string } = {
                      room: '🏨 Oda',
                      sunbed: '🏖️ Şezlong',
                      breakfast: '🍳 Kahvaltı',
                      dinner: '🍽️ Akşam Yemeği'
                    };
                    const statusLabels: { [key: string]: { text: string; color: string } } = {
                      pending_payment: { text: 'Ödeme Bekliyor', color: '#f39c12' },
                      paid: { text: 'Ödendi', color: '#27ae60' },
                      confirmed: { text: 'Onaylandı', color: '#27ae60' },
                      paid_pending_confirmation: { text: 'Onay Bekliyor', color: '#3498db' },
                      cancelled: { text: 'İptal', color: '#e74c3c' },
                      completed: { text: 'Tamamlandı', color: '#95a5a6' },
                      pending: { text: 'Beklemede', color: '#f39c12' }
                    };
                    const paymentLabels: { [key: string]: { text: string; color: string } } = {
                      pending: { text: 'Bekliyor', color: '#f39c12' },
                      paid: { text: 'Ödendi', color: '#27ae60' },
                      failed: { text: 'Başarısız', color: '#e74c3c' }
                    };

                    const childMap: { [parentId: string]: any[] } = {};
                    const topLevel: any[] = [];
                    bookings.forEach(b => {
                      const parentId = b.customer_info?.parentBookingId;
                      if (parentId && b.customer_info?.autoCreated) {
                        if (!childMap[parentId]) childMap[parentId] = [];
                        childMap[parentId].push(b);
                      } else {
                        topLevel.push(b);
                      }
                    });

                    return topLevel.map((booking: any) => {
                      const children = childMap[booking.id] || [];
                      const hasChildren = children.length > 0;
                      const isExpanded = expandedRooms.has(booking.id);

                      return (
                        <div key={booking.id} className={`booking-card bc-${booking.product_type}`}>
                          {/* Header */}
                          <div className="booking-card-header">
                            <div>
                              <div className="booking-card-id">{booking.reservation_id || '-'}</div>
                              {booking.asistpro_confirmation_code && booking.asistpro_confirmation_code !== booking.reservation_id && (
                                <div style={{ fontSize: '0.72rem', color: '#27ae60', fontWeight: 'bold', marginTop: '2px' }} title="AsistPro Rezervasyon No">
                                  📋 {booking.asistpro_confirmation_code}
                                </div>
                              )}
                              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>
                                {booking.created_at && new Date(booking.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                            <div className="booking-card-type">{productTypeLabels[booking.product_type]}</div>
                          </div>

                          {/* Badges */}
                          <div className="booking-card-badges">
                            <span className="booking-card-badge" style={{ backgroundColor: statusLabels[booking.status]?.color || '#666' }}>
                              {statusLabels[booking.status]?.text || booking.status}
                            </span>
                            <span className="booking-card-badge" style={{ backgroundColor: paymentLabels[booking.payment_status]?.color || '#666' }}>
                              {paymentLabels[booking.payment_status]?.text || booking.payment_status || '-'}
                            </span>
                            {booking.invoice_type && (
                              <span className="booking-card-badge"
                                style={{ backgroundColor: booking.invoice_status === 'created' ? '#27ae60' : booking.invoice_status === 'draft' ? '#e67e22' : booking.invoice_status === 'failed' ? '#e74c3c' : '#95a5a6' }}
                                title={booking.invoice_status === 'created' ? `Paraşüt ID: ${booking.parasut_invoice_id}` : booking.invoice_status === 'draft' ? 'Fatura oluştu ama resmileştirilemedi (taslak)' : ''}>
                                {booking.invoice_status === 'created' ? (
                                  booking.invoice_pdf_url
                                    ? <a href={booking.invoice_pdf_url} target="_blank" rel="noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>🧾 Fatura PDF</a>
                                    : '🧾 Fatura Kesildi'
                                ) : booking.invoice_status === 'draft' ? '⚠️ Taslak Fatura' : booking.invoice_status === 'failed' ? '🧾 Fatura Hatası' : '🧾 Fatura Bekliyor'}
                              </span>
                            )}
                            {hasChildren && (
                              <span className="booking-card-badge" style={{ backgroundColor: '#3498db' }}>
                                +{children.length} alt ürün
                              </span>
                            )}
                          </div>

                          {/* Customer Info */}
                          <div className="booking-card-row">
                            <span className="booking-card-label">👤 Müşteri:</span>
                            <span className="booking-card-value">{booking.customer_name || '-'}</span>
                          </div>
                          {booking.customer_phone && (
                            <div className="booking-card-row">
                              <span className="booking-card-label">📞 Telefon:</span>
                              <span className="booking-card-value">{booking.customer_phone}</span>
                            </div>
                          )}
                          {booking.customer_email && (
                            <div className="booking-card-row">
                              <span className="booking-card-label">📧 E-posta:</span>
                              <span className="booking-card-value" style={{ fontSize: '0.8rem' }}>{booking.customer_email}</span>
                            </div>
                          )}

                          {/* Date Info */}
                          {booking.product_type === 'room' && booking.customer_info?.checkIn ? (
                            <div className="booking-card-row">
                              <span className="booking-card-label">📅 Tarih:</span>
                              <span className="booking-card-value">
                                {new Date(booking.customer_info.checkIn.split('T')[0] + 'T12:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                                {' → '}
                                {booking.customer_info.checkOut ? new Date(booking.customer_info.checkOut.split('T')[0] + 'T12:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) : ''}
                                {booking.customer_info?.nights ? ` (${booking.customer_info.nights}G)` : ''}
                              </span>
                            </div>
                          ) : booking.booking_date ? (
                            <div className="booking-card-row">
                              <span className="booking-card-label">📅 Tarih:</span>
                              <span className="booking-card-value">{new Date(booking.booking_date).toLocaleDateString('tr-TR')}</span>
                            </div>
                          ) : null}

                          {/* Details */}
                          {booking.product_type === 'sunbed' && (booking.customer_info?.zoneDetails || booking.items?.zones) && (() => {
                            const zones: any[] = booking.customer_info?.zoneDetails || booking.items?.zones || [];
                            const totalPersons = booking.items?.totalPersons || zones.reduce((s: number, z: any) => s + (z.person_count || 2), 0);
                            const zoneText = zones.length === 1
                              ? (zones[0].label || `Bölge ${zones[0].zone_id}`)
                              : `${zones.length} bölge`;
                            return (
                              <div className="booking-card-row">
                                <span className="booking-card-label">🏖️ Şezlong:</span>
                                <span className="booking-card-value">{zoneText} · {totalPersons} kişi</span>
                              </div>
                            );
                          })()}
                          {booking.product_type === 'sunbed' && !booking.customer_info?.zoneDetails && !booking.items?.zones && booking.items?.sunbeds && (
                            <div className="booking-card-row">
                              <span className="booking-card-label">🏖️ Şezlong:</span>
                              <span className="booking-card-value">{booking.items.sunbeds.length} şezlong</span>
                            </div>
                          )}
                          {(booking.product_type === 'breakfast' || booking.product_type === 'dinner') && (
                            <div className="booking-card-row">
                              <span className="booking-card-label">👥 Kişi:</span>
                              <span className="booking-card-value">
                                {(() => {
                                  const items = booking.items || {};
                                  const adults = (items.adults || 0) + (items.men || 0) + (items.women || 0);
                                  const children = items.children?.length || (items.children0_6 || 0) + (items.children7_12 || 0);
                                  const total = items.totalPersons || adults + children || 1;
                                  return adults > 0 && children > 0 ? `${adults} yetişkin + ${children} çocuk` : `${total} kişi`;
                                })()}
                              </span>
                            </div>
                          )}
                          {booking.product_type === 'room' && (
                            <div className="booking-card-row">
                              <span className="booking-card-label">🏠 Oda:</span>
                              <span className="booking-card-value">
                                {booking.room_number || booking.items?.roomTypeName || booking.room_type_label || ''}
                                {booking.customer_info?.adults ? ` ${booking.customer_info.adults}Y` : ''}
                                {booking.customer_info?.children ? `+${booking.customer_info.children}Ç` : ''}
                              </span>
                            </div>
                          )}
                          {booking.product_type === 'room' && booking.customer_info?.mealDetails && (
                            <div style={{ marginTop: '0.3rem', padding: '0.5rem', background: '#fff8e1', borderRadius: '6px', fontSize: '0.8rem' }}>
                              {booking.customer_info.mealDetails.breakfast && (
                                <div>
                                  🍳 <strong>Kahvaltı:</strong>{' '}
                                  {booking.customer_info.mealDetails.breakfast.adults > 0 ? `${booking.customer_info.mealDetails.breakfast.adults} yetişkin` : ''}
                                  {booking.customer_info.mealDetails.breakfast.children7_12 > 0 ? ` + ${booking.customer_info.mealDetails.breakfast.children7_12} çocuk(7-12)` : ''}
                                  {booking.customer_info.mealDetails.breakfast.children0_6 > 0 ? ` + ${booking.customer_info.mealDetails.breakfast.children0_6} çocuk(0-6)` : ''}
                                  {` · ${booking.customer_info.mealDetails.breakfast.nights} gece`}
                                </div>
                              )}
                              {booking.customer_info.mealDetails.dinner && (
                                <div style={{ marginTop: '0.2rem' }}>
                                  🍖 <strong>Akşam:</strong>{' '}
                                  {booking.customer_info.mealDetails.dinner.adults > 0 ? `${booking.customer_info.mealDetails.dinner.adults} yetişkin` : ''}
                                  {booking.customer_info.mealDetails.dinner.children7_12 > 0 ? ` + ${booking.customer_info.mealDetails.dinner.children7_12} çocuk(7-12)` : ''}
                                  {booking.customer_info.mealDetails.dinner.children0_6 > 0 ? ` + ${booking.customer_info.mealDetails.dinner.children0_6} çocuk(0-6)` : ''}
                                  {` · ${booking.customer_info.mealDetails.dinner.nights} gece`}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Price */}
                          <div className="booking-card-price">
                            ₺{(booking.total_price || 0).toLocaleString('tr-TR')}
                          </div>

                          {/* Children (Expand/Collapse) */}
                          {hasChildren && (
                            <>
                              <button
                                className="booking-card-expand-btn"
                                onClick={() => setExpandedRooms(prev => {
                                  const next = new Set(prev);
                                  if (next.has(booking.id)) next.delete(booking.id); else next.add(booking.id);
                                  return next;
                                })}
                              >
                                {isExpanded ? '▼' : '▶'} {children.length} Alt Ürün {isExpanded ? 'Gizle' : 'Göster'}
                              </button>
                              {isExpanded && (
                                <div className="booking-card-children">
                                  {children.map((child: any, idx: number) => (
                                    <div key={idx} className="booking-card-child">
                                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{productTypeLabels[child.product_type]}</div>
                                      <div style={{ fontSize: '0.85rem' }}>
                                        <div>📅 {child.booking_date ? new Date(child.booking_date).toLocaleDateString('tr-TR') : '-'}</div>
                                        <div>💰 ₺{(child.total_price || 0).toLocaleString('tr-TR')}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {/* Actions */}
                          <div className="booking-card-actions">
                            {booking.status !== 'cancelled' && (
                              <button
                                onClick={async () => {
                                  const reason = window.prompt('İptal sebebini yazın (opsiyonel):');
                                  if (window.confirm(`Bu rezervasyonu iptal etmek istediğinize emin misiniz?\n\n${booking.reservation_id}`)) {
                                    try {
                                      await adminAPI.cancelBooking(booking.id.toString(), reason || undefined);
                                      alert('Rezervasyon iptal edildi');
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('İptal hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ backgroundColor: '#f39c12' }}
                              >❌ İptal</button>
                            )}
                            {booking.status === 'cancelled' && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Bu rezervasyonu kalıcı olarak silmek istediğinize emin misiniz?\n\n${booking.reservation_id}\n\n⚠️ Bu işlem geri alınamaz!`)) {
                                    try {
                                      await adminAPI.deleteBooking(booking.id.toString());
                                      alert('Rezervasyon silindi');
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('Silme hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ backgroundColor: '#e74c3c' }}
                              >🗑️ Sil</button>
                            )}
                            {booking.status === 'paid_pending_confirmation' && (
                              <button
                                onClick={async () => {
                                  try {
                                    if (booking.product_type === 'room') {
                                      if (window.confirm(`Bu rezervasyon ödemesi alınmış ancak AsistPro onayı eksik görünüyor.\n\n${booking.reservation_id}\n\nAsistPro eşleme/onay işlemi şimdi yeniden denenecek. Devam edilsin mi?`)) {
                                        const result = await adminAPI.manualConfirmBooking(booking.id.toString());
                                        alert('✅ ' + (result.data?.message || 'Rezervasyon toparlandı'));
                                      }
                                    } else {
                                      await adminAPI.updateBookingStatus(booking.id.toString(), 'confirmed');
                                      alert('Rezervasyon onaylandı');
                                    }
                                    fetchBookings();
                                  } catch (err: any) {
                                    alert('Onay hatası: ' + (err.response?.data?.message || err.message));
                                  }
                                }}
                                style={{ backgroundColor: '#27ae60' }}
                              >{booking.product_type === 'room' ? '🔄 Toparla' : '✅ Onayla'}</button>
                            )}
                            {(booking.status === 'pending_payment' || booking.status === 'pending') && booking.product_type === 'room' && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Bu rezervasyonu manuel olarak onaylamak istediğinize emin misiniz?\n\n${booking.reservation_id}\n\nÖdeme alındığından emin olun!`)) {
                                    try {
                                      const result = await adminAPI.manualConfirmBooking(booking.id.toString());
                                      alert('✅ ' + (result.data?.message || 'Rezervasyon onaylandı'));
                                      fetchBookings();
                                    } catch (err: any) {
                                      alert('Onay hatası: ' + (err.response?.data?.message || err.message));
                                    }
                                  }
                                }}
                                style={{ backgroundColor: '#2980b9' }}
                              >🔧 Manuel Onayla</button>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Manuel Şezlong Rezervasyon Modal */}
      <ManualSunbedBookingModal
        isOpen={showManualSunbedModal}
        onClose={() => setShowManualSunbedModal(false)}
        onSuccess={() => {
          setShowManualSunbedModal(false);
          fetchBookings();
        }}
      />

      {/* Manuel Rezervasyon Modal */}
      <ManualRoomBookingModal
        isOpen={showManualBookingModal}
        onClose={() => setShowManualBookingModal(false)}
        onSuccess={(data) => {
          setReservationData(data);
          setShowManualBookingModal(false);
          setShowSuccessModal(true);
          // Rezervasyon listesini yenile
          fetchBookings();
        }}
      />

      {/* Başarı Modal */}
      <ReservationSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setReservationData(null);
        }}
        reservationData={reservationData}
      />
    </div>
  );
};

export default AdminPanel;
